import * as d3 from 'd3';
import { Prediction, ProcessedPrediction } from '../../../types/predictions';
import { Theme } from '../../../theme';
import { renderRangePredictions } from './RangePredictionRenderer';
import { renderPriceTimeRangePredictions } from './PriceTimeRangePredictionRenderer';
import { renderSuccessfulPredictions } from './SuccessfulPredictionRenderer';
// ARCHIVED: Contest import removed (April 2026 contest ended)
// import { renderContestTargetLine } from './ContestTargetLine';

import { parseDate } from '../../../utils/dateUtils';
import { formatPrice } from '../../../utils/priceUtils';
import { predictionStyles } from './predictionStyles';
import { createTweetVisualization, TweetData } from './TweetVisualization';

// Helper function to get numeric price value from prediction
// Prioritizes actual numeric fields over formatted price_prediction string
const getNumericPrice = (prediction: any): number | null => {
  // First try the numeric fields (these are actual numbers)
  if (prediction.price_target !== undefined && prediction.price_target !== null) {
    const num = Number(prediction.price_target);
    if (!isNaN(num)) return num;
  }
  if (prediction.price_min !== undefined && prediction.price_min !== null) {
    const num = Number(prediction.price_min);
    if (!isNaN(num)) return num;
  }
  if (prediction.price_max !== undefined && prediction.price_max !== null) {
    const num = Number(prediction.price_max);
    if (!isNaN(num)) return num;
  }
  
  // Last resort: try to parse price_prediction string (e.g., "$100,000" or "$1")
  if (prediction.price_prediction) {
    const cleaned = String(prediction.price_prediction).replace(/[$,\s]/g, '');
    const num = parseFloat(cleaned);
    if (!isNaN(num)) return num;
  }
  
  return null;
};

// ========== ADAPTIVE RENDERING SYSTEM ==========
type DetailLevel = 'MINIMAL' | 'COMPACT' | 'STANDARD' | 'DETAILED';

interface AdaptiveRenderingConfig {
  level: DetailLevel;
  showLabels: boolean;
  showTweets: boolean;
  showDescriptions: boolean;
  iconSize: number;
  fontSize: string;
  spacing: number;
}

// Smart zoom level detection
const getCurrentZoomLevel = (xScale: any, yScale: any, chartWidth: number, chartHeight: number): number => {
  // Calculate zoom based on domain vs range ratio
  const xDomain = xScale.domain();
  const yDomain = yScale.domain();
  
  const timeSpan = new Date(xDomain[1]).getTime() - new Date(xDomain[0]).getTime();
  const priceRange = yDomain[1] - yDomain[0];
  
  // Normalize zoom level (0 = fully zoomed out, 1 = fully zoomed in)
  const timeZoom = Math.min(1, (10 * 365 * 24 * 60 * 60 * 1000) / timeSpan); // 10 years as max
  const priceZoom = Math.min(1, 1000000 / priceRange); // $1M as max range
  
  return (timeZoom + priceZoom) / 2;
};

// Get adaptive configuration based on zoom level - SIMPLIFIED VERSION
const getAdaptiveConfig = (zoomLevel: number, elementDensity: number = 1): AdaptiveRenderingConfig => {
  // Keep it simple - just show tweets always
  return {
    level: 'DETAILED',
    showLabels: true,
    showTweets: true, 
    showDescriptions: true,
    iconSize: 12,
    fontSize: '12px',
    spacing: 60
  };
};

// Calculate element density for smart positioning
const calculateElementDensity = (predictions: Prediction[], xScale: any, yScale: any): number => {
  if (predictions.length <= 1) return 0;
  
  const positions = predictions.map(p => ({
    x: xScale(new Date(p.end_date || p.start_date || new Date())),
    y: yScale(Number(p.price_target || p.price_min || p.price_max || 0))
  }));
  
  // Calculate average distance between elements
  let totalDistance = 0;
  let comparisons = 0;
  
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const distance = Math.sqrt(
        Math.pow(positions[i].x - positions[j].x, 2) + 
        Math.pow(positions[i].y - positions[j].y, 2)
      );
      totalDistance += distance;
      comparisons++;
    }
  }
  
  const avgDistance = totalDistance / comparisons;
  return Math.max(0, Math.min(2, (200 - avgDistance) / 100)); // Normalize to 0-2 scale
};

// ========== ADAPTIVE RENDERING FUNCTIONS ==========

// Smart overlap detection and positioning
const detectAndResolveOverlaps = (elements: Array<{x: number, y: number, width?: number, height?: number}>, config: AdaptiveRenderingConfig) => {
  const OVERLAP_THRESHOLD = config.spacing;
  const overlaps: number[][] = [];
  
  // Find overlapping elements
  for (let i = 0; i < elements.length; i++) {
    for (let j = i + 1; j < elements.length; j++) {
      const distance = Math.sqrt(
        Math.pow(elements[i].x - elements[j].x, 2) + 
        Math.pow(elements[i].y - elements[j].y, 2)
      );
      
      if (distance < OVERLAP_THRESHOLD) {
        overlaps.push([i, j]);
      }
    }
  }
  
  // Resolve overlaps by staggering positions
  const resolved = [...elements];
  overlaps.forEach(([i, j]) => {
    const stagger = config.spacing / 2;
    resolved[j].y += stagger * (j % 2 === 0 ? 1 : -1);
    resolved[j].x += stagger * (j % 3 === 0 ? 1 : 0);
  });
  
  return resolved;
};

// Priority scoring for elements (higher = more important)
const getPredictionPriority = (prediction: any): number => {
  let priority = 0;
  
  // High-value predictions get priority
  const value = Number(prediction.price_target || prediction.price_min || prediction.price_max || 0);
  if (value > 500000) priority += 3;
  else if (value > 200000) priority += 2;
  else if (value > 100000) priority += 1;
  
  // Famous influencers get priority
  const name = prediction.influencer?.name || prediction.expert_name || '';
  if (['Michael Saylor', 'Cathie Wood', 'Plan B'].some(famous => name.includes(famous))) {
    priority += 2;
  }
  
  // Recent/active predictions get priority
  if (prediction.success_status === 'pending') priority += 1;
  if (prediction.is_preview) priority += 1;
  
  return priority;
};

// Adaptive target prediction renderer
const renderTargetPredictionAdaptive = (
  prediction: any, 
  predictionGroup: any, 
  x: number, 
  y: number, 
  config: AdaptiveRenderingConfig, 
  theme: any,
  isPreview: boolean = false
) => {
  const priority = getPredictionPriority(prediction);
  
  // MINIMAL: Just a small colored dot
  if (config.level === 'MINIMAL') {
    predictionGroup
      .append('circle')
      .attr('cx', x)
      .attr('cy', y)
      .attr('r', config.iconSize)
      .attr('fill', isPreview ? '#ff9800' : 
                   prediction.success_status === 'success' ? '#4caf50' : 
                   prediction.success_status === 'miss' ? '#f44336' : theme.primary)
      .attr('opacity', 0.8)
      .attr('class', `prediction-target-minimal ${isPreview ? 'preview' : ''}`);
    return;
  }
  
  // COMPACT: Small dot + abbreviated name  
  if (config.level === 'COMPACT') {
    // Circle
    predictionGroup
      .append('circle')
      .attr('cx', x)
      .attr('cy', y)
      .attr('r', config.iconSize)
      .attr('fill', isPreview ? '#ff9800' : 
                   prediction.success_status === 'success' ? '#4caf50' : 
                   prediction.success_status === 'miss' ? '#f44336' : theme.primary)
      .attr('stroke', theme.primary)
      .attr('stroke-width', 1)
      .attr('opacity', isPreview ? 0.7 : 1);
    
    // Abbreviated name (only for high priority)
    if (priority >= 2) {
      const name = prediction.influencer?.name || prediction.expert_name || '';
      const abbrev = name.split(' ').map(word => word[0]).join('').substring(0, 2);
      
      predictionGroup
        .append('text')
        .attr('x', x)
        .attr('y', y - 15)
        .attr('text-anchor', 'middle')
        .attr('font-size', config.fontSize)
        .attr('font-weight', 'bold')
        .attr('fill', theme.text)
        .text(abbrev);
    }
    return;
  }
  
  // STANDARD and DETAILED: Full rendering (existing logic)
  // Circle
  predictionGroup
    .append('circle')
    .attr('cx', x)
    .attr('cy', y)
    .attr('r', config.iconSize)
    .attr('fill', isPreview ? '#ff9800' : 
                 prediction.success_status === 'success' ? '#4caf50' : 
                 prediction.success_status === 'miss' ? '#f44336' : theme.primary)
    .attr('stroke', theme.primary)
    .attr('stroke-width', 2)
    .attr('opacity', isPreview ? 0.7 : 1);
  
  // Full label (only if showing labels)
  if (config.showLabels) {
    const numericPrice = getNumericPrice(prediction);
    const targetDate = new Date(prediction.end_date);
    const formattedDate = targetDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric', 
      year: 'numeric'
    });
    const price = numericPrice !== null ? formatPrice(numericPrice) : 'N/A';
    const influencerName = prediction.influencer?.name || prediction.expert_name || 'Expert';
    const previewPrefix = isPreview ? 'PREVIEW: ' : '';
    const combinedText = `${previewPrefix}${influencerName}\n${price}\nby ${formattedDate}`;
    
    // Only show grey label if there's no tweet data (to avoid duplicate with tweet cards)
    const hasTweetData = prediction.quote || prediction.source_url;
    if (!hasTweetData) {
      createStyledTextLabel(predictionGroup, combinedText, x + 15, y - 10, theme, {
        fontSize: config.fontSize
      });
    }
  }
};

// Adaptive range prediction renderer
const renderRangePredictionAdaptive = (
  prediction: any,
  predictionGroup: any, 
  x1: number,
  x2: number,
  y1: number, 
  y2: number,
  config: AdaptiveRenderingConfig,
  theme: any
) => {
  const priority = getPredictionPriority(prediction);
  
  // MINIMAL: Just thin line between min/max prices
  if (config.level === 'MINIMAL') {
    predictionGroup.append('line')
      .attr('x1', x1)
      .attr('x2', x2)
      .attr('y1', y1)
      .attr('y2', y2)
      .attr('stroke', theme.primary)
      .attr('stroke-width', 1)
      .attr('opacity', 0.6);
    return;
  }
  
  // COMPACT: Thin rectangle, no labels unless high priority
  if (config.level === 'COMPACT') {
    // Rectangle
    const rectHeight = Math.abs(y2 - y1);
    const rectY = Math.min(y1, y2);
    
    predictionGroup.append('rect')
      .attr('x', x1)
      .attr('y', rectY)
      .attr('width', x2 - x1)
      .attr('height', rectHeight)
      .attr('fill', 'rgba(247, 147, 26, 0.1)')
      .attr('stroke', theme.primary)
      .attr('stroke-width', 1);
    
    // Label only for high priority
    if (priority >= 2) {
      const name = prediction.influencer?.name || prediction.expert_name || '';
      const abbrev = name.split(' ').map(word => word[0]).join('').substring(0, 2);
      
      predictionGroup.append('text')
        .attr('x', (x1 + x2) / 2)
        .attr('y', rectY - 5)
        .attr('text-anchor', 'middle')
        .attr('font-size', config.fontSize)
        .attr('fill', theme.text)
        .text(abbrev);
    }
    return;
  }
  
  // STANDARD/DETAILED: Full rectangle with labels
  const rectHeight = Math.abs(y2 - y1);
  const rectY = Math.min(y1, y2);
  
  predictionGroup.append('rect')
    .attr('x', x1)
    .attr('y', rectY)
    .attr('width', x2 - x1)
    .attr('height', rectHeight)
    .attr('fill', prediction.success_status === 'success' ? 'rgba(76, 175, 80, 0.1)' :
                  prediction.success_status === 'miss' ? 'rgba(244, 67, 54, 0.1)' :
                  'rgba(247, 147, 26, 0.1)')
    .attr('stroke', prediction.success_status === 'success' ? '#4caf50' : 
                   prediction.success_status === 'miss' ? '#f44336' : theme.primary)
    .attr('stroke-width', 1.5);
  
  // Labels for STANDARD/DETAILED
  if (config.showLabels) {
    const influencerName = prediction.influencer?.name || prediction.expert_name || 'Expert';
    const priceRange = `${formatPrice(Number(prediction.price_min))}-${formatPrice(Number(prediction.price_max))}`;
    const combinedText = `${influencerName}\n${priceRange}`;
    
    const labelX = Math.min(x2 + 10, x1 + ((x2 - x1) / 2));
    const labelY = (y1 + y2) / 2;
    
    // Only show grey label if there's no tweet data (to avoid duplicate with tweet cards)
    const hasTweetData = prediction.quote || prediction.source_url;
    if (!hasTweetData) {
      createStyledTextLabel(predictionGroup, combinedText, labelX, labelY, theme, {
        fontSize: config.fontSize
      });
    }
  }
};

// Adaptive bet/wager prediction renderer  
const renderBetPredictionAdaptive = (
  prediction: any,
  predictionGroup: any,
  x: number,
  y: number, 
  config: AdaptiveRenderingConfig,
  theme: any
) => {
  const priority = getPredictionPriority(prediction);
  
  // MINIMAL: Just dice emoji
  if (config.level === 'MINIMAL') {
    predictionGroup.append('text')
      .attr('x', x)
      .attr('y', y + 3)
      .attr('text-anchor', 'middle')
      .attr('font-size', `${config.iconSize}px`)
      .text('🎲');
    return;
  }
  
  // COMPACT: Small circle with dice
  if (config.level === 'COMPACT') {
    predictionGroup.append('circle')
      .attr('cx', x)
      .attr('cy', y)
      .attr('r', config.iconSize)
      .attr('fill', predictionStyles.colors.pending)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 1);
      
    predictionGroup.append('text')
      .attr('x', x)
      .attr('y', y + 2)
      .attr('text-anchor', 'middle')
      .attr('font-size', '8px')
      .text('🎲');
    return;
  }
  
  // STANDARD/DETAILED: Full bet marker rendering
  const isWinner = prediction.success_status === 'success';
  const isLoser = prediction.success_status === 'miss';
  const betColor = isWinner ? predictionStyles.colors.betWinnerSolid : 
                  isLoser ? predictionStyles.colors.betLoserSolid : 
                  predictionStyles.colors.pending;
  
  // Bet circle
  predictionGroup.append('circle')
    .attr('cx', x)
    .attr('cy', y)
    .attr('r', config.iconSize)
    .attr('fill', betColor)
    .attr('stroke', '#ffffff')
    .attr('stroke-width', 2);
  
  // Dice icon
  predictionGroup.append('text')
    .attr('x', x)
    .attr('y', y + 3)
    .attr('text-anchor', 'middle')
    .attr('font-size', '10px')
    .attr('fill', '#ffffff')
    .text('🎲');
  
  // Labels and badges only in DETAILED mode
  if (config.level === 'DETAILED' && config.showLabels) {
    // Winner/Loser badge
    if (isWinner || isLoser) {
      const badgeText = isWinner ? '✓ WIN' : '✗ LOSS';
      const badgeY = y + 25;
      
      predictionGroup.append('rect')
        .attr('x', x - 17.5)
        .attr('y', badgeY - 8)
        .attr('width', 35)
        .attr('height', 16)
        .attr('fill', betColor)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1)
        .attr('rx', 3);
      
      predictionGroup.append('text')
        .attr('x', x)
        .attr('y', badgeY + 2)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('font-weight', 'bold')
        .attr('fill', '#ffffff')
        .text(badgeText);
    }
    
    // Name label removed - tweet cards already show the name
    // No duplicate grey labels needed
  }
};

// Global interface for tweet position tracking
declare global {
  interface Window {
    tweetPositions: Array<{
      x: number;
      y: number;
      width: number;
      height: number;
      index: number;
    }>;
  }
}

/**
 * Helper function to create text labels with clean background styling that adapts to theme
 */
const createStyledTextLabel = (
  parent: d3.Selection<SVGGElement, unknown, null, undefined>,
  text: string,
  x: number,
  y: number,
  theme: Theme,
  options: {
    anchor?: string;
    fontSize?: string;
    fontWeight?: string;
    padding?: number;
  } = {}
) => {
  const {
    anchor = 'middle',
    fontSize = '11px',
    fontWeight = 'bold',
    padding = 4
  } = options;

  // FORCE dark mode for now - let's just make it work!
  // We'll detect it properly later, but first let's see dark backgrounds
  const isDarkMode = true; // FORCING DARK MODE TO TEST
  
  
  // Use chart background gray color with transparency
  const textColor = '#ffffff';  // White text
  const backgroundColor = 'rgba(26, 26, 26, 0.75)';  // Chart gray with 75% opacity for transparency  
  const borderColor = 'rgba(255, 255, 255, 0.15)';  // Subtle white border
  

  // Create text element first to measure it
  const textElement = parent.append('text')
    .attr('x', x)
    .attr('y', y)
    .attr('text-anchor', anchor)
    .attr('font-size', fontSize)
    .attr('font-weight', fontWeight)
    .attr('fill', textColor)
    .attr('dy', '0.35em')
    .style('pointer-events', 'none')
    .text(text);

  // Get bounding box for background
  const bbox = (textElement.node() as SVGTextElement).getBBox();
  
  // Add background rectangle before text (safer insertion method)
  const backgroundRect = parent.append('rect')
    .attr('x', bbox.x - padding)
    .attr('y', bbox.y - padding/2)
    .attr('width', bbox.width + padding * 2)
    .attr('height', bbox.height + padding)
    .attr('fill', backgroundColor)
    .attr('stroke', borderColor)
    .attr('stroke-width', 1)
    .attr('rx', 3)
    .attr('ry', 3)
    .style('filter', `drop-shadow(0px 1px 2px ${isDarkMode ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'})`)
    .style('pointer-events', 'none');
  
  // Move background behind text by adjusting the DOM order
  if (textElement.node() && backgroundRect.node()) {
    try {
      const textNode = textElement.node();
      const rectNode = backgroundRect.node();
      if (textNode && rectNode && textNode.parentNode) {
        textNode.parentNode.insertBefore(rectNode, textNode);
      }
    } catch (insertError) {
      console.warn('Could not reorder background rect, but text is still visible:', insertError);
    }
  }

  return textElement;
};

interface RenderProps {
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>;
  predictions: Prediction[];
  activePredictions: number[];
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  theme: Theme;
  chartData?: Array<{ time: Date; value: number }>;
  showAllDots?: boolean; // Dashboard mode: show dots for all predictions
  compactMode?: boolean; // Mobile landscape: smaller fonts and cards
}

// Define the RenderOptions interface that was missing
interface RenderOptions {
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>;
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  theme: Theme;
  predictions: Prediction[];
  activePredictions: number[];
  chartData: Array<{ time: Date; value: number }>;
}

/**
 * Detect and resolve overlapping predictions
 * Returns positions with offsets to prevent visual overlap
 */
const resolveOverlappingPredictions = (predictions: Prediction[], xScale: any, yScale: any) => {
  const positionedPredictions = predictions.map(prediction => {
    // Handle different prediction types
    let priceValue: number;
    let targetDate: Date;
    
    if (prediction.prediction_type === 'bottom') {
      priceValue = Number(prediction.price_min);
      targetDate = new Date(prediction.start_date || prediction.end_date || new Date());
    } else if (prediction.prediction_type === 'top') {
      priceValue = Number(prediction.price_max);
      targetDate = new Date(prediction.start_date || prediction.end_date || new Date());
    } else if (prediction.prediction_type === 'pricetimerange') {
      priceValue = Number(prediction.price_min);
      targetDate = new Date(prediction.end_date || prediction.target_time || new Date());
    } else if (prediction.prediction_type === 'bet') {
      // Bet predictions use price_prediction for the bet amount
      priceValue = Number(prediction.price_prediction || prediction.price_min);
      targetDate = new Date(prediction.end_date || prediction.target_time || new Date());
    } else {
      // target predictions
      priceValue = Number(prediction.price_target || prediction.price_min);
      targetDate = new Date(prediction.end_date || prediction.target_time || new Date());
    }
    
    return {
      prediction,
      originalX: xScale(targetDate),
      originalY: yScale(priceValue),
      priceValue,
      targetDate,
      adjustedX: xScale(targetDate),
      adjustedY: yScale(priceValue),
      labelOffset: 0
    };
  });

  // Sort by Y position (price level) to detect overlaps
  positionedPredictions.sort((a, b) => a.originalY - b.originalY);

  // Detect and resolve overlaps with better stacking
  const OVERLAP_THRESHOLD = 25; // pixels
  const LABEL_OFFSET_STEP = 60; // Reduced from 80px for tighter spacing
  const MAX_OFFSET = 180; // Maximum offset to prevent labels going off-screen

  for (let i = 1; i < positionedPredictions.length; i++) {
    const current = positionedPredictions[i];
    const previous = positionedPredictions[i - 1];
    
    // Check if predictions are vertically close (overlapping)
    if (Math.abs(current.originalY - previous.adjustedY) < OVERLAP_THRESHOLD) {
      
      // ✅ IMPROVED: Use incremental offsets with alternating direction
      const stackLevel = previous.labelOffset / LABEL_OFFSET_STEP + 1;
      
      if (stackLevel <= 3) {
        // For first 3 overlaps, use incremental right offsets
        current.labelOffset = Math.min(stackLevel * LABEL_OFFSET_STEP, MAX_OFFSET);
      } else {
        // For 4+ overlaps, cap at max offset and use tighter Y spacing
        current.labelOffset = MAX_OFFSET;
        current.adjustedY = previous.adjustedY + (OVERLAP_THRESHOLD / 2); // Tighter Y spacing
      }
      
      // Slightly offset the Y position for visual separation
      if (stackLevel <= 3) {
        current.adjustedY = previous.adjustedY + OVERLAP_THRESHOLD;
      }
    }
  }

  return positionedPredictions;
};

/**
 * Pre-process predictions to ensure dates are proper Date objects
 * and handle any data inconsistencies from the imported data
 */
const preprocessPredictions = (predictions: Prediction[]): ProcessedPrediction[] => {
  // Debug raw predictions before processing
  console.log('🔍 Raw predictions before processing:', predictions.map(p => ({
    id: p.id,
    type: p.prediction_type,
    end_date: p.end_date,
    end_date_type: typeof p.end_date,
    end_date_json: JSON.stringify(p.end_date)
  })));

  const processed = predictions.map(prediction => {
    // Convert dates to Date objects with fallbacks
    const startDate = prediction.start_date ? new Date(prediction.start_date) : new Date();
    const endDate = prediction.end_date ? new Date(prediction.end_date) : 
                   prediction.target_time ? new Date(prediction.target_time) : new Date();

    // Extract numeric value from price_prediction if available
    let price_prediction_num: number | undefined;
    if (prediction.price_prediction) {
      // For range predictions, use the minimum value for chart positioning
      const rangeMatch = prediction.price_prediction.match(/(\d+(?:[.,]\d+)*)\s*-\s*(\d+(?:[.,]\d+)*)/);
      if (rangeMatch) {
        price_prediction_num = parseFloat(rangeMatch[1].replace(/[.,]/g, ''));
      } else {
        // Single price extraction
        const match = prediction.price_prediction.match(/[\d,]+/);
        if (match) {
          price_prediction_num = parseFloat(match[0].replace(/,/g, ''));
        }
      }
    }

    // Create processed prediction with proper typing
    const processed: ProcessedPrediction = {
      ...prediction,
      start_date: startDate,
      end_date: endDate,
      price_prediction_num,
      // Add safety checks
      isNegativePrediction: (prediction.price_min !== undefined && prediction.price_min < 0) || 
                           (prediction.price_target !== undefined && prediction.price_target < 0),
      exclude_from_chart: prediction.exclude_from_chart || false
    } as ProcessedPrediction;

    return processed;
  });
  
  return processed;
};

/**
 * Render all prediction types
 */
export const renderAllPredictions = (props: RenderProps): void => {
  const {
    chartArea,
    predictions,
    activePredictions,
    xScale,
    yScale,
    theme,
    chartData,
    showAllDots = false,
    compactMode = false
  } = props;
  
  // ✅ COMPACT MODE: Base scale factor for mobile landscape (smaller cards/fonts)
  const compactModeScale = compactMode ? 0.65 : 1.0;
  // Store in window for access in callbacks (TypeScript closure workaround)
  (window as any).__compactModeScale = compactModeScale;

  // ========== ADAPTIVE RENDERING INTELLIGENCE ==========
  const chartWidth = Number(chartArea.attr('data-width')) || Number(chartArea.attr('width')) || 800;
  const chartHeight = Number(chartArea.attr('data-height')) || Number(chartArea.attr('height')) || 400;
  
  // 🎯 GLOBAL HORIZONTAL POSITIONING: Initialize shared positioning system
  // Reset for new render cycle - all prediction types will use this
  if (!window.tweetPositions) {
    window.tweetPositions = [];
  }
  window.tweetPositions = []; // Reset for new render
  
  // Calculate current zoom level and element density
  const zoomLevel = getCurrentZoomLevel(xScale, yScale, chartWidth, chartHeight);
  const elementDensity = calculateElementDensity(predictions, xScale, yScale);
  
  // Get adaptive configuration
  const adaptiveConfig = getAdaptiveConfig(zoomLevel, elementDensity);
  
  // ====================================================



  // Helper function to detect preview predictions
  const isPreviewPrediction = (prediction: any) => prediction.id === -1;

  // ========== MADE DATE VERTICAL LINE HELPER ==========
  // Renders a vertical line showing when prediction was made (works for ALL prediction types)
  const renderMadeDateMarker = (
    prediction: any, 
    predictionGroup: any,
    targetX: number, // X position of the target/end date
    targetY: number  // Y position of the prediction target price
  ) => {
    if (!prediction.prediction_made) return;
    
    const predictionMadeDate = new Date(prediction.prediction_made);
    const madeX = xScale(predictionMadeDate);
    
    // Skip if made date is outside visible range
    const [xMin, xMax] = xScale.domain();
    if (predictionMadeDate < xMin || predictionMadeDate > xMax) return;
    
    // Find the price when prediction was made
    let predictionMadePrice: number | null = null;
    if (chartData && chartData.length > 0) {
      const targetTime = predictionMadeDate.getTime();
      let closest = chartData[0];
      let minDiff = Math.abs(chartData[0].time.getTime() - targetTime);
      
      for (const dataPoint of chartData) {
        const diff = Math.abs(dataPoint.time.getTime() - targetTime);
        if (diff < minDiff) {
          minDiff = diff;
          closest = dataPoint;
        }
      }
      predictionMadePrice = closest ? closest.value : null;
    }
    
    if (predictionMadePrice === null) return;
    
    const madeY = yScale(predictionMadePrice);
    
    // Get Y scale domain for vertical line bounds
    const [yMin, yMax] = yScale.domain();
    const chartTop = yScale(yMax);
    const chartBottom = yScale(yMin);
    
    // Draw vertical dashed line from top to price point
    predictionGroup
      .append('line')
      .attr('x1', madeX)
      .attr('x2', madeX)
      .attr('y1', chartTop + 40) // Start from top of chart + padding
      .attr('y2', madeY)
      .attr('stroke', '#888888')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,3')
      .attr('opacity', 0.7)
      .attr('class', 'made-date-line')
      .style('pointer-events', 'none');
    
    // Circle marker at the price point where prediction was made
    predictionGroup
      .append('circle')
      .attr('cx', madeX)
      .attr('cy', madeY)
      .attr('r', 5)
      .attr('fill', '#888888')
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 2)
      .attr('opacity', 0.9)
      .attr('class', 'made-date-marker')
      .style('pointer-events', 'none');
    
    // Draw connection line to target (curved arc)
    const midX = (madeX + targetX) / 2;
    const midY = Math.min(madeY, targetY) - 40; // Arc above both points
    
    predictionGroup
      .append('path')
      .attr('d', `M ${madeX} ${madeY} Q ${midX} ${midY} ${targetX} ${targetY}`)
      .attr('stroke', '#888888')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '4,2')
      .attr('fill', 'none')
      .attr('opacity', 0.6)
      .attr('class', 'made-date-arc')
      .style('pointer-events', 'none');
    
    // Format the made date
    const formattedMadeDate = predictionMadeDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    
    // Label at top of vertical line
    const labelText = `Made: ${formattedMadeDate}`;
    const labelY = chartTop + 30;
    
    // Add text label
    predictionGroup
      .append('text')
      .attr('x', madeX)
      .attr('y', labelY)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', 'bold')
      .attr('fill', '#666666')
      .attr('opacity', 0.9)
      .attr('class', 'made-date-label')
      .style('pointer-events', 'none')
      .text(labelText);
    
    // Add background for label
    const labelNode = predictionGroup.select('.made-date-label').node() as SVGTextElement;
    if (labelNode) {
      const labelBBox = labelNode.getBBox();
      predictionGroup
        .insert('rect', '.made-date-label')
        .attr('x', labelBBox.x - 6)
        .attr('y', labelBBox.y - 3)
        .attr('width', labelBBox.width + 12)
        .attr('height', labelBBox.height + 6)
        .attr('fill', 'rgba(255, 255, 255, 0.95)')
        .attr('stroke', '#888888')
        .attr('stroke-width', 1)
        .attr('rx', 4)
        .attr('class', 'made-date-label-bg')
        .style('pointer-events', 'none');
    }
    
    // Calculate and show lead time (days between made and target)
    const targetDate = new Date(prediction.end_date || prediction.start_date);
    const leadTimeMs = targetDate.getTime() - predictionMadeDate.getTime();
    const leadTimeDays = Math.round(leadTimeMs / (1000 * 60 * 60 * 24));
    
    if (leadTimeDays > 7) { // Only show lead time if > 1 week
      const leadTimeText = `${leadTimeDays} days`;
      
      predictionGroup
        .append('text')
        .attr('x', midX)
        .attr('y', midY - 10)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', 'bold')
        .attr('fill', '#666666')
        .attr('opacity', 0.8)
        .attr('class', 'lead-time-label')
        .style('pointer-events', 'none')
        .text(leadTimeText);
      
      // Background for lead time label
      const leadNode = predictionGroup.select('.lead-time-label').node() as SVGTextElement;
      if (leadNode) {
        const leadBBox = leadNode.getBBox();
        predictionGroup
          .insert('rect', '.lead-time-label')
          .attr('x', leadBBox.x - 4)
          .attr('y', leadBBox.y - 2)
          .attr('width', leadBBox.width + 8)
          .attr('height', leadBBox.height + 4)
          .attr('fill', 'rgba(255, 255, 255, 0.9)')
          .attr('stroke', '#888888')
          .attr('stroke-width', 0.5)
          .attr('rx', 3)
          .attr('class', 'lead-time-label-bg')
          .style('pointer-events', 'none');
      }
    }
  };

  // Clear existing prediction elements (including crash and sequence predictions)
  chartArea.selectAll('.prediction-line, .prediction-target, .prediction-range, .prediction-label, .prediction-group, .tweet-visualization, .tweet-connection, .sentiment-tweets-group, .bet-wager-group, .all-dots-group, .crash-prediction, .sequence-prediction, .made-date-line, .made-date-marker, .tweet-group, .tweet-overlay-layer').remove();

  // Create overlay layer for tweet cards (appended last to ensure they're on top)
  const tweetOverlayLayer = chartArea.append('g').attr('class', 'tweet-overlay-layer');

  // 🎯 DASHBOARD MODE: Render dots for ALL predictions first (before filtering)
  if (showAllDots && chartData && chartData.length > 0) {
    const allDotsGroup = chartArea.append('g').attr('class', 'all-dots-group');
    
    // 📊 JITTER: Group predictions by day to spread multiple tweets within the same day
    const predictionsByDay = new Map<string, Array<{prediction: any, index: number, date: Date}>>();
    
    predictions.forEach((prediction, index) => {
      // Get the date for this prediction
      let predictionDate: Date | undefined;
      const dateStr = prediction.prediction_made || prediction.created_at || prediction.start_date;
      if (dateStr) {
        predictionDate = parseDate(dateStr);
      }
      if (!predictionDate) return;
      
      // Create a day key (YYYY-MM-DD)
      const dayKey = predictionDate.toISOString().split('T')[0];
      
      if (!predictionsByDay.has(dayKey)) {
        predictionsByDay.set(dayKey, []);
      }
      predictionsByDay.get(dayKey)!.push({ prediction, index, date: predictionDate });
    });
    
    // Calculate the width of one day in pixels for jitter spacing
    const sampleDate = new Date();
    const oneDayMs = 24 * 60 * 60 * 1000;
    const dayWidthPixels = Math.abs(xScale(new Date(sampleDate.getTime() + oneDayMs)) - xScale(sampleDate));
    
    // Render dots with jitter for same-day tweets
    predictionsByDay.forEach((dayPredictions, dayKey) => {
      const numTweets = dayPredictions.length;
      
      // Sort by timestamp within the day (if full timestamp available)
      dayPredictions.sort((a, b) => a.date.getTime() - b.date.getTime());
      
      dayPredictions.forEach(({ prediction, index, date: predictionDate }, tweetIndexInDay) => {
        // Find the price at this date
        const targetTime = predictionDate.getTime();
        let priceAtTime = chartData[0]?.value || 50000;
        let minDiff = Infinity;
        
        for (const point of chartData) {
          const diff = Math.abs(point.time.getTime() - targetTime);
          if (diff < minDiff) {
            minDiff = diff;
            priceAtTime = point.value;
          }
        }
        
        // Calculate X position with jitter for multiple tweets on same day
        let xPos = xScale(predictionDate);
        
        if (numTweets > 1) {
          // Spread tweets across 80% of the day width
          const spreadWidth = dayWidthPixels * 0.8;
          const spacing = spreadWidth / (numTweets + 1);
          const offset = (tweetIndexInDay + 1) * spacing - spreadWidth / 2;
          xPos += offset;
        }
        
        const yPos = yScale(priceAtTime);
        
        // Check if this is the active prediction
        const isActive = activePredictions.includes(prediction.id);
        
        // Check if this is a successful official prediction
        const isSuccessfulPrediction = prediction.success_status === 'success';
        const isMissedPrediction = prediction.success_status === 'miss';
        const isOfficialPrediction = (prediction as any).is_official_prediction === true;
        
        // Get engagement level for dot sizing (0-1 normalized)
        const engagementLevel = (prediction as any).tweet_data?.engagement_level || 0;
        const engagementScore = (prediction as any).tweet_data?.engagement_score || 0;
        
        // Calculate dot size based on engagement (min 3, max 10 for inactive; 6-12 for active)
        // Official predictions get larger base size to stand out
        const baseSize = isActive ? 6 : (isOfficialPrediction ? 8 : 3);
        const maxSize = isActive ? 12 : (isOfficialPrediction ? 12 : 10);
        const engagementBonus = engagementLevel * (maxSize - baseSize);
        const dotSize = baseSize + engagementBonus;
        
        // Calculate opacity based on engagement (min 0.3, max 0.9 for inactive)
        // Official predictions always have higher opacity
        const baseOpacity = isActive ? 1 : (isOfficialPrediction ? 0.9 : 0.3);
        const maxOpacity = isActive ? 1 : 0.9;
        const opacityBonus = engagementLevel * (maxOpacity - baseOpacity);
        const dotOpacity = baseOpacity + opacityBonus;
        
        // Determine dot color based on status
        // Success = green, Miss = red, Official pending = purple, Regular tweets = orange
        let dotFillColor: string;
        let dotStrokeColor: string;
        
        if (isSuccessfulPrediction) {
          // Green for successful predictions
          dotFillColor = isActive ? 'rgba(46, 204, 113, 1)' : `rgba(46, 204, 113, ${dotOpacity})`;
          dotStrokeColor = isActive ? '#fff' : `rgba(255, 255, 255, ${dotOpacity * 0.5})`;
        } else if (isMissedPrediction) {
          // Red for missed predictions
          dotFillColor = isActive ? 'rgba(231, 76, 60, 1)' : `rgba(231, 76, 60, ${dotOpacity})`;
          dotStrokeColor = isActive ? '#fff' : `rgba(255, 255, 255, ${dotOpacity * 0.5})`;
        } else if (isOfficialPrediction) {
          // Purple for pending official predictions
          dotFillColor = isActive ? 'rgba(155, 89, 182, 1)' : `rgba(155, 89, 182, ${dotOpacity})`;
          dotStrokeColor = isActive ? '#fff' : `rgba(255, 255, 255, ${dotOpacity * 0.5})`;
        } else {
          // Orange for regular tweets
          dotFillColor = isActive ? '#F7931A' : `rgba(247, 147, 26, ${dotOpacity})`;
          dotStrokeColor = isActive ? '#fff' : `rgba(255, 255, 255, ${dotOpacity * 0.5})`;
        }
        
        // Draw dot on price line with engagement-based sizing
        allDotsGroup.append('circle')
          .attr('cx', xPos)
          .attr('cy', yPos)
          .attr('r', dotSize)
          .attr('fill', dotFillColor)
          .attr('stroke', dotStrokeColor)
          .attr('stroke-width', isActive ? 2 : Math.max(1, engagementLevel * 2))
          .attr('class', `all-dot ${isActive ? 'active-dot' : 'inactive-dot'} ${engagementLevel > 0.5 ? 'high-engagement' : ''} ${numTweets > 1 ? 'jittered' : ''} ${isSuccessfulPrediction ? 'success-dot' : ''} ${isOfficialPrediction ? 'official-dot' : ''}`)
          .attr('data-id', prediction.id)
          .attr('data-engagement', engagementScore)
          .attr('data-day-count', numTweets)
          .attr('data-success-status', prediction.success_status || 'none')
          .style('cursor', 'pointer')
          .style('transition', 'all 0.2s ease');
      });
    });
  }

  // Filter predictions to only render active ones that are not excluded
  const visiblePredictions = predictions.filter(prediction => {
    const isActive = activePredictions.includes(prediction.id);
    const isExcluded = prediction.exclude_from_chart === true;
    const hasValidData = !!(prediction.price_target || prediction.price_min || prediction.price_max || prediction.price_prediction || prediction.prediction_type === 'sentiment' || prediction.prediction_type === 'bet');
    
    return isActive && !isExcluded && hasValidData;
  });

  // ✅ GLOBAL CHRONOLOGICAL SORTING: Sort ALL visible predictions by historical date first
  // This ensures consistent left-to-right chronological order across all prediction types
  const chronologicallySortedPredictions = [...visiblePredictions].sort((a, b) => {
    // Get the historical date for each prediction
    let dateA, dateB;
    
    if (a.prediction_type === 'sentiment') {
      // For sentiment: use target_time (historical tweet date)
      dateA = new Date(a.target_time || a.prediction_made || a.submission_date || 0).getTime();
    } else {
      // For other types: use prediction_made or submission_date
      dateA = new Date(a.prediction_made || a.submission_date || 0).getTime();
    }
    
    if (b.prediction_type === 'sentiment') {
      // For sentiment: use target_time (historical tweet date)
      dateB = new Date(b.target_time || b.prediction_made || b.submission_date || 0).getTime();
    } else {
      // For other types: use prediction_made or submission_date
      dateB = new Date(b.prediction_made || b.submission_date || 0).getTime();
    }
    
    return dateA - dateB; // Oldest first (leftmost)
  });

  // Group predictions by type for organized rendering (chronological order maintained by tweet system)
  const targetPredictions = chronologicallySortedPredictions.filter(p => p.prediction_type === 'target');
  const rangePredictions = chronologicallySortedPredictions.filter(p => p.prediction_type === 'range');
  const priceTimeRangePredictions = chronologicallySortedPredictions.filter(p => p.prediction_type === 'pricetimerange');
  const topPredictions = chronologicallySortedPredictions.filter(p => p.prediction_type === 'top');
  const bottomPredictions = chronologicallySortedPredictions.filter(p => p.prediction_type === 'bottom');
  // Crash predictions - bearish targets (price will DROP to this level)
  const crashPredictions = chronologicallySortedPredictions.filter(p => p.prediction_type === 'crash');
  // Sequence predictions - multi-step predictions
  const sequencePredictions = chronologicallySortedPredictions.filter(p => p.prediction_type === 'sequence');
  // Note: sentimentTweets now handled by unified card system above
  
  // ⭐ SMART BET PREDICTION RENDERING:
  // If ANY bet prediction is activated, find ALL participants in that bet wager
  const activeBetPredictions = visiblePredictions.filter(p => p.prediction_type === 'bet');
  const betPredictions: any[] = [];
  
  if (activeBetPredictions.length > 0) {
    // Find all bet wager groups that have at least one active participant
    const betWagerKeys = new Set<string>();
    
    activeBetPredictions.forEach(activeBet => {
      const betAmount = activeBet.price_prediction || activeBet.price_target || activeBet.price_min;
      const betDate = activeBet.end_date;
      const wagerKey = `${betAmount}_${betDate}`;
      betWagerKeys.add(wagerKey);
    });
    
    // Now include ALL participants from those wager groups (active or not)
    predictions.forEach(prediction => {
      if (prediction.prediction_type === 'bet' && !prediction.exclude_from_chart) {
        const betAmount = prediction.price_prediction || prediction.price_target || prediction.price_min;
        const betDate = prediction.end_date;
        const wagerKey = `${betAmount}_${betDate}`;
        
        if (betWagerKeys.has(wagerKey)) {
          betPredictions.push(prediction);
        }
      }
    });
    
    console.log('🎲 Bet predictions debug:', {
      activeBetPredictions: activeBetPredictions.length,
      foundBetWagers: betWagerKeys.size,
      totalBetParticipants: betPredictions.length,
      participants: betPredictions.map(p => p.influencer?.name)
    });
  }

  // SENTIMENT TWEETS - Now handled through tweet visualization with correct price positioning
  // Sentiment tweets are now rendered through the tweet visualization system
  // which correctly calculates the price at the sentiment date

  // ========== RENDER TARGET PREDICTIONS (ADAPTIVE) ==========
  targetPredictions.forEach(prediction => {
    try {
      // Get numeric price value using helper that handles both numeric fields and formatted strings
      const numericPrice = getNumericPrice(prediction);
      
      if (numericPrice === null || !prediction.end_date) {
        return;
      }

      // Use end_date (target date) for target predictions - this is when the prediction should be achieved
      const targetDate = new Date(prediction.end_date);
      const x = xScale(targetDate);
      const y = yScale(numericPrice);

      // Create group for this prediction to prevent duplication
      const predictionGroup = chartArea.append('g')
        .attr('class', 'prediction-group target-prediction')
        .attr('data-id', prediction.id)
        .style('pointer-events', 'all');

      // 🎯 ADAPTIVE RENDERING: Use smart rendering based on zoom level
      const isPreview = isPreviewPrediction(prediction);
      renderTargetPredictionAdaptive(prediction, predictionGroup, x, y, adaptiveConfig, theme, isPreview);

      // Add made date marker for ALL target predictions (shows when prediction was made)
      if (prediction.prediction_made) {
        renderMadeDateMarker(prediction, predictionGroup, x, y);
      }

      // Create multi-line label structure matching pricetimerange style
      const formattedDate = targetDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
      const price = formatPrice(numericPrice);
      const influencerName = prediction.influencer?.name || 'Expert';
      const previewPrefix = isPreview ? 'PREVIEW: ' : '';
      const combinedText = `${previewPrefix}${influencerName}\n${price}\nby ${formattedDate}`;
      
      // ✅ SPECIAL: Put Hal Finney and Michael Saylor labels on the LEFT to avoid cutoff
      const isHighValuePrediction = prediction.id === 101 || // Hal Finney
                                   (influencerName.toLowerCase().includes('saylor') || 
                                    influencerName.toLowerCase().includes('michael saylor'));
      
      const labelX = isHighValuePrediction ? x - 15 : x + 15; // LEFT for high-value, RIGHT for others
      const labelY = y - 10;
      const textAnchor = isHighValuePrediction ? 'end' : 'start'; // RIGHT-align text when on left side
      
      
      // Create label group
      const labelGroup = predictionGroup
        .append('g')
        .attr('class', 'prediction-label');
      
      // Create multi-line text element with orange text on dark background
      const textElement = labelGroup
        .append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('font-size', '13px')
        .attr('font-weight', 'bold')
        .attr('fill', '#ff8c00') // Orange text
        .attr('text-anchor', textAnchor)
        .attr('class', 'prediction-multiline-label');
      
      // Add each line as a tspan for proper multi-line support
      const lines = combinedText.split('\n');
      lines.forEach((line, index) => {
        textElement
          .append('tspan')
          .attr('x', labelX)
          .attr('dy', index === 0 ? 0 : '1.2em') // Line spacing
          .attr('font-size', index === 1 ? '14px' : '12px') // Price slightly larger
          .attr('font-weight', index === 1 ? 'bold' : index === 0 ? 'bold' : 'normal')
          .attr('fill', index === 1 ? '#ffaa33' : '#ff8c00') // Brighter orange for price
          .text(line);
      });
      
      // Add dark background that matches the chart theme
      const textBBox = textElement.node()!.getBBox();
      labelGroup
        .insert('rect', '.prediction-multiline-label')
        .attr('x', textBBox.x - 6)
        .attr('y', textBBox.y - 3)
        .attr('width', textBBox.width + 12)
        .attr('height', textBBox.height + 6)
        .attr('fill', 'rgba(26, 26, 26, 0.85)') // Dark background
        .attr('stroke', 'rgba(255, 255, 255, 0.15)')
        .attr('stroke-width', 1)
        .attr('rx', 4)
        .attr('ry', 4);

    } catch (error) {
      console.error('Error rendering target prediction:', error, prediction);
    }
  });

  // Render CRASH predictions (bearish price targets - price will DROP to target)
  console.log('📉 Rendering crash predictions:', {
    total: crashPredictions.length,
    predictions: crashPredictions.map(p => ({id: p.id, name: p.influencer?.name, target: p.price_prediction}))
  });
  
  crashPredictions.forEach(prediction => {
    try {
      const numericPrice = prediction.price_prediction || prediction.price_target || prediction.price_min;
      if (!prediction.end_date || !numericPrice) {
        console.warn('⚠️ Crash prediction missing required fields:', prediction.id);
        return;
      }
      const targetDate = new Date(prediction.end_date);
      
      if (isNaN(targetDate.getTime())) {
        console.warn('⚠️ Crash prediction has invalid date:', prediction.id);
        return;
      }
      
      const x = xScale(targetDate);
      const y = yScale(numericPrice);
      
      // Color logic for crash predictions - RED/BEARISH theme
      // Success = price dropped to/below target (bearish call was correct)
      // Miss = price didn't drop to target
      const isSuccess = prediction.success_status === 'success';
      const isMiss = prediction.success_status === 'miss';
      const crashColor = isSuccess ? '#22c55e' : // Green if bearish call was correct
                        isMiss ? '#ef4444' :     // Red if bearish call was wrong
                        '#f44336';               // Default RED for pending (bearish indicator)
      
      // Create crash prediction group
      const crashGroup = chartArea.append('g')
        .attr('class', 'crash-prediction')
        .attr('data-id', prediction.id);

      // Add made date marker for ALL crash predictions (shows when prediction was made)
      if (prediction.prediction_made) {
        renderMadeDateMarker(prediction, crashGroup, x, y);
      }

      // Draw downward arrow indicator (bearish)
      const arrowSize = 12;
      crashGroup.append('polygon')
        .attr('points', `${x},${y + arrowSize} ${x - arrowSize/2},${y} ${x + arrowSize/2},${y}`)
        .attr('fill', crashColor)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1.5)
        .attr('opacity', 0.9);
      
      // Add crash target marker circle
      crashGroup.append('circle')
        .attr('cx', x)
        .attr('cy', y)
        .attr('r', 6)
        .attr('fill', crashColor)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 2)
        .attr('opacity', 0.95);
      
      // Horizontal target line (dashed)
      crashGroup.append('line')
        .attr('x1', x - 50)
        .attr('y1', y)
        .attr('x2', x + 50)
        .attr('y2', y)
        .attr('stroke', crashColor)
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '6,3')
        .attr('opacity', 0.7);
      
      // Label with influencer name and target
      const influencerName = prediction.influencer?.name || 'Expert';
      const formattedDate = targetDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const price = formatPrice(numericPrice);
      const labelText = `${influencerName}: ↓${price} by ${formattedDate}`;
      
      // Position label above the marker
      const labelY = y - 20;
      
      // Add text label
      const label = crashGroup.append('text')
        .attr('x', x)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', 'bold')
        .attr('fill', crashColor)
        .text(labelText);
      
      // Add background for label
      const textNode = label.node() as SVGTextElement;
      if (textNode) {
        const textBBox = textNode.getBBox();
        crashGroup
          .insert('rect', 'text')
          .attr('x', textBBox.x - 6)
          .attr('y', textBBox.y - 3)
          .attr('width', textBBox.width + 12)
          .attr('height', textBBox.height + 6)
          .attr('fill', 'rgba(26, 26, 26, 0.85)')
          .attr('stroke', crashColor)
          .attr('stroke-width', 1)
          .attr('rx', 4)
          .attr('ry', 4);
      }
      
      // Add "BEARISH" badge
      crashGroup.append('rect')
        .attr('x', x - 30)
        .attr('y', y + 15)
        .attr('width', 60)
        .attr('height', 16)
        .attr('fill', crashColor)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1)
        .attr('rx', 3);
      
      crashGroup.append('text')
        .attr('x', x)
        .attr('y', y + 26)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('font-weight', 'bold')
        .attr('fill', '#ffffff')
        .text('📉 BEARISH');
        
    } catch (error) {
      console.error('Error rendering crash prediction:', error, prediction);
    }
  });

  // Render BET predictions (special wager visualization)
  console.log('🎲 Rendering bet predictions:', {
    total: betPredictions.length,
    predictions: betPredictions.map(p => ({id: p.id, name: p.influencer?.name, type: p.prediction_type, start: p.start_date, end: p.end_date, price: p.price_prediction})),
    activePredictions: activePredictions
  });
  
  // Helper function to render individual bet marker
  const renderBetMarker = (prediction: any, parentGroup: any, x: number, y: number, theme: any, isLeft: boolean) => {
    const isWinner = prediction.success_status === 'success';
    const isLoser = prediction.success_status === 'miss';
    const betColor = isWinner ? predictionStyles.colors.betWinnerSolid : 
                    isLoser ? predictionStyles.colors.betLoserSolid : 
                    predictionStyles.colors.pending;
    
    // Create individual bet group
    const betGroup = parentGroup.append('g')
      .attr('class', 'individual-bet-marker')
      .attr('data-id', prediction.id);
    
    // Bet circle with dice styling
    betGroup.append('circle')
      .attr('cx', x)
      .attr('cy', y)
      .attr('r', 8)
      .attr('fill', betColor)
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 2.5)
      .attr('opacity', 0.95);
    
    // Dice icon
    betGroup.append('text')
      .attr('x', x)
      .attr('y', y + 3)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#ffffff')
      .text('🎲');
    
    // Winner/Loser badge
    if (isWinner || isLoser) {
      const badgeText = isWinner ? '✓ WIN' : '✗ LOSS';
      const badgeY = y + 25;
      
      // Badge background
      betGroup.append('rect')
        .attr('x', x - 17.5)
        .attr('y', badgeY - 8)
        .attr('width', 35)
        .attr('height', 16)
        .attr('fill', betColor)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1)
        .attr('rx', 3);
      
      // Badge text
      betGroup.append('text')
        .attr('x', x)
        .attr('y', badgeY + 2)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('font-weight', 'bold')
        .attr('fill', '#ffffff')
        .text(badgeText);
    }
    
    // Name label with proper background
    const nameText = prediction.influencer?.name || 'Unknown';
    
    // Determine text color based on theme (black for light mode, white for dark mode)
    const textColor = theme.background === '#F5F5F5' || theme.background === '#FFFFFF' ? '#000000' : '#FFFFFF';
    
    // Remove the background rectangle - just use text with theme-appropriate color
    // Name label without background
    betGroup.append('text')
      .attr('x', x)
      .attr('y', y - 24)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('font-weight', 'bold')
      .attr('fill', textColor)
      .attr('stroke', textColor === '#000000' ? '#FFFFFF' : '#000000') // Add subtle outline for better readability
      .attr('stroke-width', 0.5)
      .text(nameText);
  };
  
  // Helper function to render bet tweet cards - SUPER SIMPLIFIED VERSION  
  const renderBetTweetCards = (predictions: any[], wagerGroup: any, x1: number, x2: number, y: number, theme: any, betAmount: any) => {
    
    // Position tweet cards CLOSE to the bet line with broader cards
    const tweetY = y + 40; // Much closer to bet line
    const tweetWidth = 320; // Much broader cards
    const spacing = 25; // Tighter spacing between cards
    
    // Find winner and loser
    const winner = predictions.find(p => p.success_status === 'success');
    const loser = predictions.find(p => p.success_status === 'miss');
    
    
    // Create cards for each participant
    let cardIndex = 0;
    const centerX = (x1 + x2) / 2;
    const startX = centerX - ((tweetWidth + spacing) * predictions.length) / 2;
    
    console.log('🎲 Bet visualization layout debug:', {
      x1, x2, y, centerX, startX, tweetY, tweetWidth, spacing
    });
    
    predictions.forEach(prediction => {
      const hasData = prediction.quote || prediction.source_url;
      const hasInfluencer = prediction.influencer?.name;
      
      console.log(`🎲 Bet prediction ${prediction.id} data check:`, {
        hasData,
        hasInfluencer,
        quote: prediction.quote ? 'YES' : 'NO',
        sourceUrl: prediction.source_url ? 'YES' : 'NO',
        influencerName: prediction.influencer?.name
      });
      
      if (hasData && hasInfluencer) {
        const cardX = startX + (cardIndex * (tweetWidth + spacing));
        
        // Simple tweet data
        const tweetData: TweetData = {
          id: prediction.id.toString(),
          predictionId: prediction.id, // For share button deep linking
          text: prediction.quote || 'Bet prediction',
          expert_name: prediction.influencer.name,
          twitter_handle: prediction.influencer.x_handle || '@unknown',
          verified: false,
          profile_image_url: prediction.influencer.profile_image,
          source_url: prediction.source_url,
          isBetPrediction: true,
          isWinner: prediction.success_status === 'success',
          isLoser: prediction.success_status === 'miss'
        };
        
        
        // Create tweet visualization directly with smaller scale for compact text
        try {
          const result = createTweetVisualization(
            wagerGroup, 
            tweetData, 
            cardX, 
            tweetY, 
            theme, 
            tweetWidth, // Broader width
            140, // Fixed compact height - increased for bet cards
            0.75 // Smaller scale factor for more compact text
          );
          
          // Add win/loss/pending indicator badge in corner
          if (result && result.cardGroup) {
            const isWinner = prediction.success_status === 'success';
            const isLoser = prediction.success_status === 'miss';
            const isPending = prediction.success_status === 'pending';
            
            const badgeSize = 24;
            const badgeX = cardX + tweetWidth - badgeSize - 8;
            const badgeY = tweetY + 8;
            
            // Badge background circle with status-appropriate color
            let badgeColor = '#6b7280'; // Default gray for pending
            if (isWinner) badgeColor = '#22c55e'; // Green for winner
            if (isLoser) badgeColor = '#ef4444';  // Red for loser
            
            result.cardGroup.append('circle')
              .attr('cx', badgeX + badgeSize/2)
              .attr('cy', badgeY + badgeSize/2)
              .attr('r', badgeSize/2)
              .attr('fill', badgeColor)
              .attr('stroke', '#ffffff')
              .attr('stroke-width', 2);
              
            // Badge symbol based on status
            let badgeSymbol = '⏳'; // Hourglass for pending
            if (isWinner) badgeSymbol = '✓'; // Check for winner
            if (isLoser) badgeSymbol = '✗';  // X for loser
              
            result.cardGroup.append('text')
              .attr('x', badgeX + badgeSize/2)
              .attr('y', badgeY + badgeSize/2 + 1)
              .attr('text-anchor', 'middle')
              .attr('dominant-baseline', 'middle')
              .attr('font-size', isPending ? '10px' : '12px') // Smaller hourglass
              .attr('font-weight', 'bold')
              .attr('fill', '#ffffff')
              .text(badgeSymbol);
          }
          
          cardIndex++;
        } catch (error) {
          console.error('❌ Error creating tweet card:', error);
          const errorObj = error as Error;
          console.error('❌ Error details:', {
            message: errorObj.message,
            stack: errorObj.stack
          });
        }
      } else {
      }
    });
    
  };

  // Helper function to render bet wager content - ZOOM-BASED SIMPLE VERSION
  const renderBetWagerContent = (predictions: any[], wagerGroup: any, xScale: any, yScale: any, theme: any) => {
    console.log('🎲 Bet wager content debug:', predictions.map(p => ({
      id: p.id,
      name: p.influencer?.name,
      quote: p.quote ? 'HAS QUOTE' : 'NO QUOTE',
      sourceUrl: p.source_url ? 'HAS URL' : 'NO URL',
      type: p.prediction_type
    })));
    
    try {
      const betAmount = predictions[0].price_prediction || predictions[0].price_target || predictions[0].price_min;
      const startDate = new Date(predictions[0].start_date);
      const endDate = new Date(predictions[0].end_date);
      
      if (!betAmount || !startDate || !endDate) {
        return;
      }
      
      const x1 = xScale(startDate);  
      const x2 = xScale(endDate);    
      const y = yScale(Number(betAmount));  
      
      // Check zoom level to determine rendering style
      const xDomain = xScale.domain();
      const timeSpan = new Date(xDomain[1]).getTime() - new Date(xDomain[0]).getTime();
      const daySpan = timeSpan / (1000 * 60 * 60 * 24);
      const isZoomedOut = daySpan > 180; // More than 6 months = zoomed out
      
      
      // Set data attribute on the existing group
      wagerGroup.attr('data-wager', `${betAmount}_${startDate}_${endDate}`);
      
      // Sort predictions by success status
      const winner = predictions.find(p => p.success_status === 'success');
      const loser = predictions.find(p => p.success_status === 'miss');
      
      console.log('🎲 Bet wager winner/loser debug:', {
        winner: winner?.influencer?.name,
        loser: loser?.influencer?.name
      });
      
      if (isZoomedOut) {
        // SIMPLE ZOOM OUT VIEW: Yellow line, dice, black background labels, and tweet cards
        
        // Simple yellow/orange line
        wagerGroup.append('line')
          .attr('class', 'bet-wager-line-simple')
          .attr('x1', x1)
          .attr('x2', x2)
          .attr('y1', y)
          .attr('y2', y)
          .attr('stroke', '#F7931A')  // Bitcoin orange/yellow
          .attr('stroke-width', 4)
          .attr('stroke-linecap', 'round')
          .attr('opacity', 0.9);
        
        // Add dice at start
        const startMarker = wagerGroup.append('g')
          .attr('transform', `translate(${x1}, ${y})`);
        
        startMarker.append('circle')
          .attr('r', 10)
          .attr('fill', predictionStyles.colors.pending)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 2);
          
        startMarker.append('text')
          .attr('y', 3)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('fill', '#ffffff')
          .text('🎲');
        
        // Add dice at end
        const endMarker = wagerGroup.append('g')
          .attr('transform', `translate(${x2}, ${y})`);
        
        endMarker.append('circle')
          .attr('r', 10)
          .attr('fill', winner ? predictionStyles.colors.betWinnerSolid : predictionStyles.colors.pending)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 2);
          
        endMarker.append('text')
          .attr('y', 3)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('fill', '#ffffff')
          .text('🎲');
        
        // Names with black background + orange text (like other predictions)
        const participants: string[] = [];
        if (winner) participants.push(`${winner.influencer?.name || 'Winner'} ✓`);
        if (loser) participants.push(`${loser.influencer?.name || 'Loser'} ✗`);
        
        if (participants.length > 0) {
          const combinedNames = participants.join(' vs ');
          const centerX = (x1 + x2) / 2; // Calculate center point
          createStyledTextLabel(wagerGroup, `🎲 ${combinedNames}`, centerX, y - 35, theme, {
            fontSize: '12px',
            anchor: 'middle'
          });
        }
        
        // ✅ ZOOMED OUT: Add tweet cards even in simple view
        
        try {
          renderBetTweetCards(predictions, wagerGroup, x1, x2, y, theme, betAmount);
        } catch (error) {
          console.error('❌ ZOOMED OUT: Error in renderBetTweetCards:', error);
        }
        
        return; // Skip detailed rendering when zoomed out
      }
      
      // DETAILED VIEW: Show full bet markers and details
      
      // Draw the main wager line (time range at $100K)
      wagerGroup.append('line')
        .attr('class', 'bet-wager-border')
        .attr('x1', x1)
        .attr('x2', x2)
        .attr('y1', y)
        .attr('y2', y)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 8)
        .attr('stroke-linecap', 'round')
        .attr('opacity', 0.3);
        
      wagerGroup.append('line')
        .attr('class', 'bet-wager-line')
        .attr('x1', x1)
        .attr('x2', x2)
        .attr('y1', y)
        .attr('y2', y)
        .attr('stroke', predictionStyles.colors.betWinnerSolid)
        .attr('stroke-width', 6)
        .attr('stroke-linecap', 'round')
        .attr('opacity', 0.9);
      
      // Add start marker (bet announcement)
      const startMarker = wagerGroup.append('g')
        .attr('class', 'bet-start-marker')
        .attr('transform', `translate(${x1}, ${y})`);
      
      startMarker.append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', 12)
        .attr('fill', predictionStyles.colors.pending)
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 3);
        
      startMarker.append('text')
        .attr('x', 0)
        .attr('y', 4)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('fill', '#ffffff')
        .text('🎲');
      
      // Add end marker - winner side (December 2024)
      if (winner) {
        const winnerMarker = wagerGroup.append('g')
          .attr('class', 'bet-winner-marker')
          .attr('transform', `translate(${x2}, ${y})`);
        
        winnerMarker.append('circle')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('r', 15)
          .attr('fill', predictionStyles.colors.betWinnerSolid)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 3);
          
        winnerMarker.append('text')
          .attr('x', 0)
          .attr('y', 4)
          .attr('text-anchor', 'middle')
          .attr('font-size', '11px')
          .attr('fill', '#ffffff')
          .attr('font-weight', 'bold')
          .text('WIN');
        
        // Winner name label with proper background
        const winnerNameText = winner.influencer?.name || 'Winner';
        const winnerTextWidth = Math.max(winnerNameText.length * 8.5, 80);
        const winnerPadding = 12;
        
        winnerMarker.append('rect')
          .attr('x', -(winnerTextWidth + winnerPadding) / 2)
          .attr('y', -38)
          .attr('width', winnerTextWidth + winnerPadding)
          .attr('height', 22)
          .attr('fill', predictionStyles.colors.successSolid)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1.5)
          .attr('rx', 6);
        
        // Winner name label
        winnerMarker.append('text')
          .attr('x', 0)
          .attr('y', -24)
          .attr('text-anchor', 'middle')
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .attr('fill', '#ffffff')
          .text(winnerNameText);
          
        // Winner badge
        winnerMarker.append('rect')
          .attr('x', -20)
          .attr('y', 20)
          .attr('width', 40)
          .attr('height', 16)
          .attr('fill', predictionStyles.colors.successSolid)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1)
          .attr('rx', 3);
          
        winnerMarker.append('text')
          .attr('x', 0)
          .attr('y', 32)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', 'bold')
          .attr('fill', '#ffffff')
          .text('✓ WIN');
      }
      
      // Add loser indicator (offset below the line)
      if (loser) {
        const loserMarker = wagerGroup.append('g')
          .attr('class', 'bet-loser-marker')
          .attr('transform', `translate(${x2}, ${y + 60})`);  // Offset below
        
        loserMarker.append('circle')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('r', 12)
          .attr('fill', predictionStyles.colors.betLoserSolid)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 3);
          
        loserMarker.append('text')
          .attr('x', 0)
          .attr('y', 4)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('fill', '#ffffff')
          .attr('font-weight', 'bold')
          .text('LOSS');
        
        // Loser name label with proper background
        const loserNameText = loser.influencer?.name || 'Loser';
        const loserTextWidth = Math.max(loserNameText.length * 8.5, 80);
        const loserPadding = 12;
        
        loserMarker.append('rect')
          .attr('x', -(loserTextWidth + loserPadding) / 2)
          .attr('y', -33)
          .attr('width', loserTextWidth + loserPadding)
          .attr('height', 22)
          .attr('fill', predictionStyles.colors.failedSolid)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1.5)
          .attr('rx', 6);
        
        // Loser name label
        loserMarker.append('text')
          .attr('x', 0)
          .attr('y', -19)
          .attr('text-anchor', 'middle')
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .attr('fill', '#ffffff')
          .text(loserNameText);
          
        // Loser badge
        loserMarker.append('rect')
          .attr('x', -20)
          .attr('y', 15)
          .attr('width', 40)
          .attr('height', 16)
          .attr('fill', predictionStyles.colors.failedSolid)
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 1)
          .attr('rx', 3);
          
        loserMarker.append('text')
          .attr('x', 0)
          .attr('y', 27)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('font-weight', 'bold')
          .attr('fill', '#ffffff')
          .text('✗ LOSS');
          
        // Connection line from main wager to loser
        wagerGroup.append('line')
          .attr('class', 'bet-loser-connection')
          .attr('x1', x2)
          .attr('x2', x2)
          .attr('y1', y + 15)
          .attr('y2', y + 45)
          .attr('stroke', predictionStyles.colors.betLoserSolid)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '4,2')
          .attr('opacity', 0.7);
      }
      
      // ✅ DETAILED VIEW: Add tweet cards in detailed view too  
      
      try {
        renderBetTweetCards(predictions, wagerGroup, x1, x2, y, theme, betAmount);
      } catch (error) {
        console.error('❌ DETAILED: Error in renderBetTweetCards:', error);
      }
      
      // Add wager label - simplified when zoomed out
      const centerX = (x1 + x2) / 2;
      if (isZoomedOut) {
        // Simple label when zoomed out
        wagerGroup.append('text')
          .attr('x', centerX)
          .attr('y', y - 15)
          .attr('text-anchor', 'middle')
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .attr('fill', '#F7931A')
          .text(`🎲 $${(Number(betAmount)/1000)}K Bet`);
      } else {
        // Detailed labels when zoomed in
        wagerGroup.append('text')
          .attr('x', centerX)
          .attr('y', y - 30)
          .attr('text-anchor', 'middle')
          .attr('font-size', '16px')
          .attr('font-weight', 'bold')
          .attr('fill', predictionStyles.colors.betWinnerSolid)
          .attr('class', 'bet-wager-label')
          .text(`🎲 $${Number(betAmount).toLocaleString()} Wager`);
          
        // Add timeframe label
        wagerGroup.append('text')
          .attr('x', centerX)
          .attr('y', y - 12)
          .attr('text-anchor', 'middle')
          .attr('font-size', '12px')
          .attr('fill', theme.text)
          .attr('opacity', 0.8)
          .text(`${startDate.toLocaleDateString()} → ${endDate.toLocaleDateString()}`);
      }
      
      
    } catch (error) {
      console.error('❌ Error rendering bet wager:', error);
    }
  };
  
  // Helper function to render individual bet content inside a D3 group (ADAPTIVE)
  const renderIndividualBetContent = (prediction: any, wagerGroup: any, xScale: any, yScale: any, theme: any) => {
    try {
      // Get numeric price value using helper that handles both numeric fields and formatted strings
      const numericPrice = getNumericPrice(prediction);
      
      if (numericPrice === null || !prediction.end_date) {
        return;
      }

      const targetDate = new Date(prediction.end_date);
      const x = xScale(targetDate);
      const y = yScale(numericPrice);

      // Get adaptive configuration for individual bets
      const zoomLevel = getCurrentZoomLevel(xScale, yScale, chartWidth, chartHeight);
      const betConfig = getAdaptiveConfig(zoomLevel);

      // Use the existing wager group and set attributes
      wagerGroup
        .attr('class', 'bet-wager-group prediction-group bet-prediction')
        .attr('data-id', prediction.id)
        .style('pointer-events', 'all');
      
      // 🎯 ADAPTIVE RENDERING: Use smart bet rendering based on zoom level  
      renderBetPredictionAdaptive(prediction, wagerGroup, x, y, betConfig, theme);

    } catch (error) {
      console.error('Error rendering individual bet:', error, prediction);
    }
  };
  
  // Group bet predictions into wagers (same price, same date)
  const betWagers = new Map();
  betPredictions.forEach(prediction => {
    const betAmount = prediction.price_prediction || prediction.price_target || prediction.price_min;
    const betDate = prediction.end_date;
    const wagerKey = `${betAmount}_${betDate}`;
    
    if (!betWagers.has(wagerKey)) {
      betWagers.set(wagerKey, []);
    }
    betWagers.get(wagerKey).push(prediction);
  });
  
  // Convert wagers to array format for D3 data binding
  const wagerArray = Array.from(betWagers.entries()).map(([key, predictions]) => ({
    key,
    predictions,
    isWager: predictions.length >= 2
  }));
  
  
  // Use D3 data binding for bet wagers to prevent duplication
  const betWagerGroups = chartArea.selectAll<SVGGElement, any>('.bet-wager-group')
    .data(wagerArray, (d: any) => d.key);
  
  // Remove old wager groups
  betWagerGroups.exit().remove();
  
  // Add new wager groups
  const newWagerGroups = betWagerGroups.enter()
    .append('g')
    .attr('class', 'bet-wager-group');
  
  // Merge existing and new groups
  const allWagerGroups = betWagerGroups.merge(newWagerGroups);
  
  // Process each wager group
  allWagerGroups.each(function(wagerData: any) {
    const wagerGroup = d3.select(this);
    // Clear existing content to prevent duplication
    wagerGroup.selectAll('*').remove();
    
    if (wagerData.isWager) {
      renderBetWagerContent(wagerData.predictions, wagerGroup, xScale, yScale, theme);
    } else {
      // Single bet prediction
      wagerData.predictions.forEach((prediction: any) => {
        renderIndividualBetContent(prediction, wagerGroup, xScale, yScale, theme);
      });
    }
  });
  


  // ========== RENDER RANGE PREDICTIONS (ADAPTIVE) ==========
  rangePredictions.forEach(prediction => {
    try {
      if (!prediction.price_min || !prediction.price_max || !prediction.start_date || !prediction.end_date) {
        return;
      }

      const startDate = new Date(prediction.start_date);
      const endDate = new Date(prediction.end_date);
      const x1 = xScale(startDate);
      const x2 = xScale(endDate);
      const y1 = yScale(Number(prediction.price_max)); // Upper bound
      const y2 = yScale(Number(prediction.price_min)); // Lower bound

      // Check if prediction is within visible range
      const [xMin, xMax] = xScale.domain();
      if (endDate < xMin || startDate > xMax) return;

      // Create group for this prediction
      const predictionGroup = chartArea.append('g')
        .attr('class', `prediction-group range-prediction`)
        .attr('data-id', prediction.id);

      // 🎯 ADAPTIVE RENDERING: Use smart rendering based on zoom level
      renderRangePredictionAdaptive(prediction, predictionGroup, x1, x2, y1, y2, adaptiveConfig, theme);

      // Add made date marker for ALL range predictions (shows when prediction was made)
      // Use the start of the range as the target position
      if (prediction.prediction_made) {
        const midY = (y1 + y2) / 2; // Middle of price range for target position
        renderMadeDateMarker(prediction, predictionGroup, x1, midY);
      }

      // Draw thin lines for min and max values
      predictionGroup.append('line')
        .attr('class', 'range-min-line')
        .attr('x1', x1)
        .attr('x2', x2)
        .attr('y1', y2)
        .attr('y2', y2)
        .attr('stroke', theme.primary)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,2');

      predictionGroup.append('line')
        .attr('class', 'range-max-line')
        .attr('x1', x1)
        .attr('x2', x2)
        .attr('y1', y1)
        .attr('y2', y1)
        .attr('stroke', theme.primary)
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,2');

      // Add price label next to the range (similar to target predictions)
      const influencerName = prediction.influencer?.name || 'Expert';
      const twitterHandle = prediction.influencer?.x_handle ? `@${prediction.influencer.x_handle}` : '';
      const priceRange = `${formatPrice(Number(prediction.price_min))}-${formatPrice(Number(prediction.price_max))}`;
      
      // Combine text for multi-line display
      const combinedText = twitterHandle 
        ? `${influencerName}\n${twitterHandle}\n${priceRange}`
        : `${influencerName}\n${priceRange}`;
      
      // ✅ SPECIAL: Put high-value predictions on the LEFT to avoid cutoff
      const isHighValueRange = prediction.id === 101 || // Hal Finney
                               (influencerName.toLowerCase().includes('saylor') || 
                                influencerName.toLowerCase().includes('michael saylor'));
      
      const labelX = isHighValueRange ? 
        x1 - 10 : // LEFT side for high-value
        Math.min(x2 + 10, xScale.range()[1] - 150); // RIGHT side for others
      const labelY = (y1 + y2) / 2; // Center vertically on the range
      const textAnchor = isHighValueRange ? 'end' : 'start';
      
      
      // Create label group
      const labelGroup = predictionGroup
        .append('g')
        .attr('class', 'prediction-label range-label');
      
      // Create multi-line text element with orange text on dark background
      const textElement = labelGroup
        .append('text')
        .attr('x', labelX)
        .attr('y', labelY)
        .attr('font-size', '13px')
        .attr('font-weight', 'bold')
        .attr('fill', '#ff8c00') // Orange text
        .attr('text-anchor', textAnchor)
        .attr('class', 'prediction-multiline-label');
      
      // Add each line as a tspan for proper multi-line support
      const lines = combinedText.split('\n');
      lines.forEach((line, index) => {
        textElement
          .append('tspan')
          .attr('x', labelX)
          .attr('dy', index === 0 ? 0 : '1.2em') // Line spacing
          .attr('font-size', index === lines.length - 1 ? '14px' : '12px') // Price line slightly larger
          .attr('font-weight', index === lines.length - 1 ? 'bold' : index === 0 ? 'bold' : 'normal')
          .attr('fill', index === lines.length - 1 ? '#ffaa33' : '#ff8c00') // Brighter orange for price
          .text(line);
      });
      
      // Add dark background that matches the chart theme
      const textBBox = textElement.node()!.getBBox();
      labelGroup
        .insert('rect', '.prediction-multiline-label')
        .attr('x', textBBox.x - 6)
        .attr('y', textBBox.y - 3)
        .attr('width', textBBox.width + 12)
        .attr('height', textBBox.height + 6)
        .attr('fill', 'rgba(26, 26, 26, 0.85)') // Dark background
        .attr('stroke', 'rgba(255, 255, 255, 0.15)')
        .attr('stroke-width', 1)
        .attr('rx', 4)
        .attr('ry', 4);

    } catch (error) {
      console.error(`Error rendering range prediction ${prediction.id}:`, error);
    }
  });

  // Render PRICE-TIME RANGE predictions (horizontal dotted lines) with overlap resolution
  if (priceTimeRangePredictions.length > 0) {
    const positionedPriceTime = resolveOverlappingPredictions(priceTimeRangePredictions, xScale, yScale);
    
    positionedPriceTime.forEach(({ prediction, originalX, originalY, adjustedY, labelOffset }) => {
      try {
        if (!prediction.price_min || !prediction.start_date || !prediction.end_date) return;

        const startDate = new Date(prediction.start_date);
        const endDate = new Date(prediction.end_date);
        const x1 = xScale(startDate);
        const x2 = xScale(endDate);
        const yPos = originalY; // Use original Y for the actual prediction line

        // Check if prediction is within visible range
        const [xMin, xMax] = xScale.domain();
        if (endDate < xMin || startDate > xMax) return;

        // Create group for this prediction with enhanced z-index
        const isPreview = isPreviewPrediction(prediction);
        const predictionGroup = chartArea.append('g')
          .attr('class', `prediction-group pricetime-prediction ${isPreview ? 'preview-prediction' : ''}`)
          .attr('data-id', prediction.id)
          .style('z-index', '1000')
          .style('pointer-events', 'all'); // Ensure interactions work

        // Draw horizontal dotted line with enhanced visibility
        predictionGroup.append('line')
          .attr('class', 'pricetime-line')
          .attr('x1', x1)
          .attr('x2', x2)
          .attr('y1', yPos)
          .attr('y2', yPos)
          .attr('stroke', isPreview ? '#ff9800' : // Orange for preview
                         prediction.success_status === 'success' ? '#4caf50' : 
                         prediction.success_status === 'miss' ? '#f44336' : theme.primary)
          .attr('stroke-width', isPreview ? 4 : 3) // Thicker for preview
          .attr('stroke-dasharray', isPreview ? '8,6' : '6,4') // Different dash pattern for preview
          .attr('stroke-linecap', 'round')
          .attr('opacity', isPreview ? 0.7 : 0.9)
          .style('z-index', '1000'); // Bring to front

        // Add made date marker for ALL priceTimeRange predictions (shows when prediction was made)
        if (prediction.prediction_made) {
          renderMadeDateMarker(prediction, predictionGroup, x1, yPos);
        }

        // Add start and end markers with enhanced visibility
        predictionGroup.append('line')
          .attr('class', 'pricetime-start-marker')
          .attr('x1', x1)
          .attr('x2', x1)
          .attr('y1', yPos - 10)
          .attr('y2', yPos + 10)
          .attr('stroke', prediction.success_status === 'success' ? '#4caf50' : 
                         prediction.success_status === 'miss' ? '#f44336' : theme.primary)
          .attr('stroke-width', 3)
          .attr('opacity', 0.9)
          .style('z-index', '1001');

        predictionGroup.append('line')
          .attr('class', 'pricetime-end-marker')
          .attr('x1', x2)
          .attr('x2', x2)
          .attr('y1', yPos - 10)
          .attr('y2', yPos + 10)
          .attr('stroke', prediction.success_status === 'success' ? '#4caf50' : 
                         prediction.success_status === 'miss' ? '#f44336' : theme.primary)
          .attr('stroke-width', 3)
          .attr('opacity', 0.9)
          .style('z-index', '1001');

        // Add label with overlap-aware positioning
        const labelGroup = predictionGroup.append('g')
          .attr('class', 'prediction-label');

        // Create multi-line label structure using the proper method (no flickering)
        const influencerName = prediction.influencer?.name || 'Expert';
        const priceText = formatPrice(Number(prediction.price_min));
        const dateText = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const combinedText = `${influencerName}\n${priceText}\nby ${dateText}`;
        
        // ✅ SPECIAL: Put high-value predictions on the LEFT to avoid cutoff
        const isHighValueBottom = prediction.id === 101 || // Hal Finney
                                 (influencerName.toLowerCase().includes('saylor') || 
                                  influencerName.toLowerCase().includes('michael saylor'));
        
        const labelX = isHighValueBottom ? 
          x2 - 40 - (labelOffset > 0 ? labelOffset : 0) : // LEFT side for high-value
          x2 + 40 + (labelOffset > 0 ? labelOffset : 0); // RIGHT side for others
        
        
        // For extremely low prices, position label ABOVE the line instead of below
        const [topPixel, bottomPixel] = yScale.range();
        const isLowPrice = yPos + 40 > bottomPixel - 20; // If label would be near/below chart bottom
        const labelY = isLowPrice 
          ? (labelOffset > 0 ? adjustedY - 40 : yPos - 40) // Above the line
          : (labelOffset > 0 ? adjustedY + 40 : yPos + 40); // Below the line (normal)
        
        // Create a custom styled label with orange text on dark background
        const textAnchor = isHighValueBottom ? 'end' : 'start';
        const textElement = labelGroup
          .append('text')
          .attr('x', labelX)
          .attr('y', labelY)
          .attr('font-size', '13px')
          .attr('font-weight', 'bold')
          .attr('fill', '#ff8c00') // Orange text
          .attr('text-anchor', textAnchor)
          .attr('class', 'prediction-multiline-label');
        
        // Add each line as a tspan for proper multi-line support
        const lines = combinedText.split('\n');
        lines.forEach((line, index) => {
          textElement
            .append('tspan')
            .attr('x', labelX)
            .attr('dy', index === 0 ? 0 : '1.2em') // Line spacing
            .attr('font-size', index === 1 ? '14px' : '12px') // Price slightly larger
            .attr('font-weight', index === 1 ? 'bold' : index === 0 ? 'bold' : 'normal')
            .attr('fill', index === 1 ? '#ffaa33' : '#ff8c00') // Brighter orange for price
            .text(line);
        });
        
        // Add dark background that matches the chart theme (no setTimeout)
        const textBBox = textElement.node()!.getBBox();
        labelGroup
          .insert('rect', '.prediction-multiline-label')
          .attr('x', textBBox.x - 6)
          .attr('y', textBBox.y - 4)
          .attr('width', textBBox.width + 12)
          .attr('height', textBBox.height + 8)
          .attr('fill', 'rgba(26, 26, 26, 0.85)') // Dark background matching chart
          .attr('stroke', '#444444') // Dark grey border
          .attr('stroke-width', 1)
          .attr('rx', 4)
          .attr('class', 'prediction-label-bg')
          .style('pointer-events', 'none');

        // Always draw connector line since label is now further away and below
        predictionGroup.append('line')
          .attr('class', 'label-connector')
          .attr('x1', x2 + 5) // Start from end marker
          .attr('x2', labelX - 5) // End near label
          .attr('y1', yPos)
          .attr('y2', labelY - 5) // Connect to top of multi-line label (now below prediction)
          .attr('stroke', theme.text)
          .attr('stroke-width', 1)
          .attr('opacity', 0.5)
          .attr('stroke-dasharray', '2,1');


      } catch (error) {
        console.error(`Error rendering pricetime prediction ${prediction.id}:`, error);
      }
    });
  }

  // Render TOP predictions (solid horizontal lines) with overlap resolution
  if (topPredictions.length > 0) {
    // Modify the resolve function to handle price_max for top predictions
    const positionedTops = topPredictions.map(prediction => {
      const priceValue = prediction.price_max;
      const targetDate = new Date(prediction.start_date || prediction.end_date || new Date());
      
      return {
        prediction,
        originalX: xScale(targetDate),
        originalY: yScale(Number(priceValue)),
        priceValue: Number(priceValue),
        targetDate,
        adjustedX: xScale(targetDate),
        adjustedY: yScale(Number(priceValue)),
        labelOffset: 0
      };
    });

    // Sort by Y position and apply overlap resolution
    positionedTops.sort((a, b) => a.originalY - b.originalY);
    const OVERLAP_THRESHOLD = 25;
    const LABEL_OFFSET_STEP = 80;

    for (let i = 1; i < positionedTops.length; i++) {
      const current = positionedTops[i];
      const previous = positionedTops[i - 1];
      
      if (Math.abs(current.originalY - previous.adjustedY) < OVERLAP_THRESHOLD) {
        current.labelOffset = (previous.labelOffset + 1) * LABEL_OFFSET_STEP;
        current.adjustedY = previous.adjustedY + OVERLAP_THRESHOLD;
      }
    }
    
    positionedTops.forEach(({ prediction, originalX, originalY, adjustedY, labelOffset }) => {
      try {
        if (!prediction.price_max || !prediction.start_date) return;

        const startDate = new Date(prediction.start_date);
        const yPos = originalY; // Use original Y for the actual prediction line

        // Get chart dimensions - line starts from start_date and goes to end_date or Dec 31st
        const [xMin, xMax] = xScale.domain();
        const endDate = prediction.end_date 
          ? new Date(prediction.end_date) 
          : new Date(new Date().getFullYear(), 11, 31); // Dec 31st of current year as fallback
        
        const x1 = xScale(startDate); // Start from the prediction start_date
        const x2 = xScale(endDate);   // Go to prediction end_date or Dec 31st

        // Check if prediction is within visible range
        const [yMin, yMax] = yScale.domain();
        if (Number(prediction.price_max) < yMin || Number(prediction.price_max) > yMax) {
          return;
        }

        // Skip if start date is after the visible range
        if (startDate > xMax) return;

        // Create group for this prediction
        const predictionGroup = chartArea.append('g')
          .attr('class', 'prediction-group top-prediction')
          .attr('data-id', prediction.id);

        // Draw solid horizontal line from start_date to end of chart
        predictionGroup.append('line')
          .attr('class', 'top-line')
          .attr('x1', Math.max(x1, xScale(xMin))) // Don't start before visible area
          .attr('x2', x2)
          .attr('y1', yPos)
          .attr('y2', yPos)
          .attr('stroke', prediction.success_status === 'success' ? '#4caf50' : 
                         prediction.success_status === 'miss' ? '#f44336' : theme.primary)
          .attr('stroke-width', 2)
          .attr('opacity', 0.8);

        // Add made date marker for ALL top predictions (shows when prediction was made)
        if (prediction.prediction_made) {
          renderMadeDateMarker(prediction, predictionGroup, Math.max(x1, xScale(xMin)), yPos);
        }

        // Label removed - using tweet card instead

        // Connector line removed - no label to connect to

        // Add break marker if top prediction failed
        if (prediction.success_status === 'miss') {
          // Break markers for failed top predictions - need to find when price exceeded the TOP level
          // For TOP predictions, we need to check when price went ABOVE the predicted maximum
          
          // First, let's see if we can find the break point dynamically from chart data
          let breakPoint: { date: string; price: number; influencer: string | undefined } | null = null;
          if (chartData && chartData.length > 0 && prediction.start_date) {
            const startDate = new Date(prediction.start_date);
            const topPrice = Number(prediction.price_max);
            
            // Find the first point where price exceeded the TOP prediction after start date
            for (const dataPoint of chartData) {
              if (dataPoint.time >= startDate && dataPoint.value > topPrice) {
                breakPoint = {
                  date: dataPoint.time.toISOString().split('T')[0],
                  price: dataPoint.value,
                  influencer: prediction.influencer?.name
                };
                break;
              }
            }
          }
          
          // If we found a break point, render the red dot
          if (breakPoint) {
            const breakDate = new Date(breakPoint.date);
            const breakX = xScale(breakDate);
            
            // Only render if the break date is within visible range
            if (breakDate >= xMin && breakDate <= xMax) {
              
              // Add red dot ON the prediction line at the exact break date
              const breakDot = predictionGroup.append('g')
                .attr('class', 'break-dot')
                .attr('transform', `translate(${breakX}, ${yPos})`);
              
              // Red circle on the prediction line
              breakDot.append('circle')
                .attr('cx', 0)
                .attr('cy', 0)
                .attr('r', 5)
                .attr('fill', '#ff4444')
                .attr('stroke', 'white')
                .attr('stroke-width', 2)
                .style('cursor', 'pointer');
              
              // Add tooltip on hover
              breakDot.append('title')
                .text(`${prediction.influencer?.name}${prediction.influencer?.x_handle ? ` (@${prediction.influencer.x_handle})` : ''}: $${Number(prediction.price_max).toLocaleString()} top broken on ${breakPoint.date} at $${breakPoint.price.toLocaleString()}`);
                
            }
          }
        }

      } catch (error) {
        console.error(`Error rendering TOP prediction ${prediction.id}:`, error);
      }
    });
  }

  // Render BOTTOM predictions (solid horizontal lines) with overlap resolution
  if (bottomPredictions.length > 0) {
    const positionedBottoms = resolveOverlappingPredictions(bottomPredictions, xScale, yScale);
    
    positionedBottoms.forEach(({ prediction, originalX, originalY, adjustedY, labelOffset }) => {
      try {
        if (!prediction.price_min || !prediction.start_date) return;

        const startDate = new Date(prediction.start_date);
        const yPos = originalY; // Use original Y for the actual prediction line

        // Get chart dimensions - line starts from start_date and goes to the end of viewport
        const [xMin, xMax] = xScale.domain();
        const x1 = xScale(startDate); // Start from the prediction start_date
        const x2 = xScale(xMax);      // Go to end of chart viewport

        // Check if prediction is within visible Y range
        const [yMin, yMax] = yScale.domain();
        if (Number(prediction.price_min) < yMin || Number(prediction.price_min) > yMax) {
          return;
        }

        // Skip if start date is after the visible range
        if (startDate > xMax) return;

        // Create group for this prediction
        const predictionGroup = chartArea.append('g')
          .attr('class', 'prediction-group bottom-prediction')
          .attr('data-id', prediction.id);

        // Draw solid horizontal line from start_date to end of chart
        predictionGroup.append('line')
          .attr('class', 'bottom-line')
          .attr('x1', Math.max(x1, xScale(xMin))) // Don't start before visible area
          .attr('x2', x2)
          .attr('y1', yPos)
          .attr('y2', yPos)
          .attr('stroke', prediction.success_status === 'success' ? '#4caf50' : 
                         prediction.success_status === 'miss' ? '#f44336' : theme.primary)
          .attr('stroke-width', 2)
          .attr('opacity', 0.8);

        // Add made date marker for ALL bottom predictions (shows when prediction was made)
        if (prediction.prediction_made) {
          renderMadeDateMarker(prediction, predictionGroup, Math.max(x1, xScale(xMin)), yPos);
        }

        // Label removed - using tweet card instead

        // Add break marker if bottom prediction failed
        if (prediction.success_status === 'miss') {
          // Break markers for failed bottom predictions
          const breakMarkerData = [
            { id: 94, date: '2025-08-26', price: 110185.354, influencer: 'Bitcoin Rachy' },
            { id: 53, date: '2025-08-02', price: 113234.605, influencer: 'Kristen' },
            { id: 76, date: '2025-08-12', price: 118773.8, influencer: 'Adam Simecka' }
          ];
          
          const breakData = breakMarkerData.find(b => b.influencer === prediction.influencer?.name);
          
          if (breakData) {
            const breakDate = new Date(breakData.date);
            const breakX = xScale(breakDate);
            
            // Only render if the break date is within visible range
            if (breakDate >= xMin && breakDate <= xMax) {
              
              // Add red dot ON the prediction line at the exact break date
              const breakDot = predictionGroup.append('g')
                .attr('class', 'break-dot')
                .attr('transform', `translate(${breakX}, ${yPos})`);
              
              // Red circle on the prediction line
              breakDot.append('circle')
                .attr('cx', 0)
                .attr('cy', 0)
                .attr('r', 5)
                .attr('fill', '#ff4444')
                .attr('stroke', 'white')
                .attr('stroke-width', 2)
                .style('cursor', 'pointer');
              
              // Add tooltip on hover (optional)
              breakDot.append('title')
                .text(`${prediction.influencer?.name}${prediction.influencer?.x_handle ? ` (@${prediction.influencer.x_handle})` : ''}: $${Number(prediction.price_min).toLocaleString()} bottom broken on ${breakData.date}`);
                
            }
          }
        }

      } catch (error) {
        console.error(`Error rendering BOTTOM prediction ${prediction.id}:`, error);
      }
    });
  }

  // INTEGRATE: Render successful predictions with green dots and crossing markers
  renderSuccessfulPredictions({
    chartArea,
    predictions,
    activePredictions,
    xScale,
    yScale,
    theme,
    chartData
  });
  

  // INTEGRATE: Render tweet visualizations for predictions with tweet data
  // Use the overlay layer so tweet cards are always on top
  const tweetLayer = chartArea.select('.tweet-overlay-layer');
  renderTweetVisualizations({
    chartArea: tweetLayer.empty() ? chartArea : tweetLayer as any,
    predictions,
    activePredictions,
    xScale,
    yScale,
    theme,
    chartData
  });

  // Apply visibility classes based on active predictions
  chartArea.selectAll('.prediction-group')
    .classed('prediction-active', function() {
      const id = parseInt(d3.select(this).attr('data-id'));
      return activePredictions.includes(id);
    })
    .classed('prediction-inactive', function() {
      const id = parseInt(d3.select(this).attr('data-id'));
      return !activePredictions.includes(id);
    });

  // ARCHIVED: Contest target line removed (April 2026 contest ended)
  // renderContestTargetLine({ chartArea, xScale, yScale, theme, chartHeight });
  
  // Move tweet overlay layer to the very end so it's painted on top of everything
  const overlayLayer = chartArea.select('.tweet-overlay-layer');
  if (!overlayLayer.empty()) {
    overlayLayer.raise(); // SVG method to move element to end of parent
  }
};

/**
 * Render tweet visualizations for predictions with tweet data
 */
const renderTweetVisualizations = ({
  chartArea,
  predictions,
  activePredictions,
  xScale,
  yScale,
  theme,
  chartData
}: {
  chartArea: d3.Selection<SVGGElement, unknown, null, undefined>;
  predictions: Prediction[];
  activePredictions: number[];
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  theme: Theme;
  chartData?: Array<{ time: Date; value: number }>;
}) => {
  // Filter predictions that have tweet data and are active (exclude bet predictions - they render with their wager)
  const tweetsToRender = predictions.filter(prediction => {
    const isActive = activePredictions.includes(prediction.id);
    const hasTweetData = prediction.quote || prediction.source_url;
    const hasInfluencer = prediction.influencer?.name;
    const isBetPrediction = prediction.prediction_type === 'bet'; // EXCLUDE bet predictions from tweet rendering
    
    // Skip bet predictions - they render with their wager lines
    if (isBetPrediction) {
      return false;
    }
    
    // Special handling for range predictions - always show labels if active and has influencer
    if (prediction.prediction_type === 'range') {
      console.log('Range prediction render debug:', {
        type: prediction.prediction_type,
        isActive,
        hasTweetData,
        hasInfluencer,
        quote: prediction.quote,
        source_url: prediction.source_url,
        influencer: prediction.influencer?.name,
        price_min: prediction.price_min,
        price_max: prediction.price_max,
        willRender: isActive && hasInfluencer // Show if active and has name, regardless of tweet data
      });
      
      return isActive && hasInfluencer; // Don't require tweet data for range predictions
    }
    
    // Special handling for bet predictions - always show if active and has influencer
    if (prediction.prediction_type === 'bet') {
      console.log('Bet prediction render debug:', {
        type: prediction.prediction_type,
        isActive,
        hasTweetData,
        hasInfluencer,
        quote: prediction.quote,
        source_url: prediction.source_url,
        influencer: prediction.influencer?.name,
        willRender: isActive && hasInfluencer // Show if active and has name, regardless of tweet data
      });
      
      return isActive && hasInfluencer; // Don't require tweet data for bet predictions, just like ranges
    }
    
    console.log('General prediction render debug:', {
      type: prediction.prediction_type,
      isActive,
      hasTweetData,
      hasInfluencer,
      isBetPrediction,
      quote: prediction.quote,
      source_url: prediction.source_url,
      influencer: prediction.influencer?.name
    });
    
    return isActive && hasTweetData && hasInfluencer;
  })
  .sort((a, b) => {
    // ✅ CHRONOLOGICAL SORTING: Sort by historical dates (oldest first = leftmost)
    let dateA, dateB;
    
    if (a.prediction_type === 'sentiment') {
      // For sentiment: use target_time (historical tweet date)
      dateA = new Date(a.target_time || a.prediction_made || a.submission_date || 0).getTime();
    } else {
      // For other types: use prediction_made or submission_date
      dateA = new Date(a.prediction_made || a.submission_date || 0).getTime();
    }
    
    if (b.prediction_type === 'sentiment') {
      // For sentiment: use target_time (historical tweet date)
      dateB = new Date(b.target_time || b.prediction_made || b.submission_date || 0).getTime();
    } else {
      // For other types: use prediction_made or submission_date
      dateB = new Date(b.prediction_made || b.submission_date || 0).getTime();
    }
    
    return dateA - dateB; // Oldest first (leftmost)
  });

  console.log('🎯 CHRONOLOGICAL TWEET SORTING:', {
    totalTweets: tweetsToRender.length,
    sortedOrder: tweetsToRender.map(p => ({ 
      id: p.id, 
      name: p.expert_name, 
      type: p.prediction_type,
      date: p.prediction_type === 'sentiment' ? p.target_time : (p.prediction_made || p.submission_date)
    }))
  });


  // Use data binding to prevent unnecessary rerendering (like lead time labels)
  const tweetGroups = chartArea.selectAll<SVGGElement, Prediction>('.tweet-group')
    .data(tweetsToRender, (d: Prediction) => `tweet-${d.id}`);
  
  // Remove tweets that are no longer needed
  tweetGroups.exit().remove();
  
  // Add new tweet groups
  const newTweetGroups = tweetGroups.enter()
    .append('g')
    .attr('class', 'tweet-group');
  
  // Merge existing and new groups  
  const allTweetGroups = tweetGroups.merge(newTweetGroups);

  // Process each tweet group (existing or new)
  allTweetGroups.each(function(prediction: any, index: number) {
    const tweetGroup = d3.select(this);
    
    // Clear existing content for updates
    tweetGroup.selectAll('*').remove();
    try {
      // Get prediction position - handle sentiment tweets differently
      let predictionDate: Date | undefined;
      let price: number;
      
      if (prediction.prediction_type === 'sentiment') {
        // For sentiment tweets, use creation date and find actual price at that time
        predictionDate = parseDate(prediction.prediction_made || prediction.created_at);
        if (!predictionDate) return;
        
        // Find actual price at sentiment time
        let priceAtTime: number = 50000; // fallback
        if (chartData && chartData.length > 0) {
          const targetTime = predictionDate.getTime();
          const now = new Date().getTime();
          const isRecentSentiment = (now - targetTime) < (24 * 60 * 60 * 1000); // Within last 24 hours
          
          if (isRecentSentiment) {
            // For recent sentiment, use latest price
            const latestPrice = chartData[chartData.length - 1];
            priceAtTime = latestPrice.value;
          } else {
            // For historical sentiment, find closest price
            let closest = chartData[0];
            let minDiff = Math.abs(closest.time.getTime() - targetTime);
            
            for (const point of chartData) {
              const diff = Math.abs(point.time.getTime() - targetTime);
              if (diff < minDiff) {
                minDiff = diff;
                closest = point;
              }
            }
            priceAtTime = closest ? closest.value : 50000;
          }
        }
        price = priceAtTime;
      } else if (prediction.prediction_type === 'target') {
        // For TARGET predictions, use target price and target date
        predictionDate = parseDate(prediction.end_date); // Target date
        if (!predictionDate) return;
        price = prediction.price_prediction || prediction.price_target || prediction.price_min || 50000;
      } else {
        // For other prediction types, use existing logic
        predictionDate = parseDate(prediction.start_date || prediction.prediction_made || prediction.created_at);
        if (!predictionDate) return;
        price = prediction.price_min || prediction.price_target || 50000; // fallback price
      }
      
      const xPos = xScale(predictionDate);
      const yPos = yScale(price);
      
      // SIMPLE STACKING: Basic left-aligned stacking without collision detection
      const chartWidth = xScale.range()[1] - xScale.range()[0];
      
      // Get zoom level to adjust card size dynamically  
      const xRange = xScale.domain();
      const daySpan = (xRange[1].getTime() - xRange[0].getTime()) / (1000 * 60 * 60 * 24);
      
      // Compact mode scale (mobile landscape)
      const modeScale = (window as any).__compactModeScale || 1.0;
      
      // Screenshot mode detected - TweetVisualization handles the boost internally now
      const isScreenshotMode = typeof window !== 'undefined' && 
        new URLSearchParams(window.location.search).get('screenshot') === 'true';
      
      // ========== SIMPLIFIED SCALING ==========
      // TweetVisualization now handles screenshot boost internally
      // We just pass a base scale factor based on zoom level
      let zoomScale = 1.0;
      if (daySpan < 30) zoomScale = 0.9;        // Zoomed in: slightly smaller
      else if (daySpan < 90) zoomScale = 1.0;   // Standard
      else if (daySpan < 180) zoomScale = 1.05; // Slightly zoomed out
      else if (daySpan > 365) zoomScale = 1.1;  // Very zoomed out: slightly larger
      
      const scaleFactor = zoomScale * modeScale;
      const baseCardWidth = isScreenshotMode ? 240 : 300; // Slightly narrower in screenshots for multi-card readability
      const screenshotBoost = isScreenshotMode ? 1.4 : 1.0;
      
      // Debug
      console.log('Tweet card scale:', { daySpan: Math.round(daySpan), scaleFactor, isScreenshotMode });
      
      // Initialize stacking system (global positioning already reset at start)
      if (!window.tweetPositions) {
        window.tweetPositions = [];
      }
      
      // No need to reset here - global reset at start of render handles this
      
      // HORIZONTAL TOP STACKING: All cards arranged horizontally at the top
      const topMargin = 20;
      const leftStartMargin = 20;
      const cardSpacing = 15;
      
      // Calculate horizontal position based on card index
      let tweetX = leftStartMargin;
      let tweetY = topMargin; // Fixed Y position at top
      
      const estimatedCardHeight = isScreenshotMode ? 200 : 140;

      if (window.tweetPositions && window.tweetPositions.length > 0) {
        // Only consider non-bet positions for horizontal stacking
        const nonBetPositions = (window.tweetPositions as any[]).filter((pos: any) => pos.isBet !== true);
        if (nonBetPositions.length > 0) {
          const lastTweet = nonBetPositions[nonBetPositions.length - 1];
          const nextX = lastTweet.x + lastTweet.width + cardSpacing;
          const chartXRange = xScale.range();
          const availableChartWidth = Math.max(...chartXRange) - Math.min(...chartXRange);
          const predictedWidth = baseCardWidth * scaleFactor * screenshotBoost;

          // Wrap to next row when a card would run off the right edge.
          if (nextX + predictedWidth > availableChartWidth - 20) {
            const bottom = Math.max(
              ...nonBetPositions.map((p: any) => p.y + (p.height || estimatedCardHeight))
            );
            tweetX = leftStartMargin;
            tweetY = bottom + cardSpacing;
          } else {
            tweetX = nextX;
            tweetY = lastTweet.y;
          }
        }
      }
      
      // Create compact tweet data for better UX
      let displayText = prediction.quote;
      
      // Keep full text content - no truncation
      if (!displayText && prediction.prediction_type === 'range') {
        const minPrice = formatPrice(Number(prediction.price_min));
        const maxPrice = formatPrice(Number(prediction.price_max));
        displayText = `Price range prediction: ${minPrice} - ${maxPrice}`;
      } else if (!displayText) {
        displayText = `Prediction: ${prediction.price_prediction || formatPrice(price)}`;
      }
      
      const tweetData: TweetData = {
        id: prediction.id.toString(),
        predictionId: prediction.id, // For share button deep linking
        text: displayText,
        expert_name: prediction.influencer?.name || prediction.expert_name || 'Expert',
        twitter_handle: prediction.influencer?.x_handle || '@unknown',
        verified: false,
        profile_image_url: prediction.influencer?.profile_image || prediction.tweet_data?.profile_image_url,
        source_url: prediction.source_url,
        prediction_date: prediction.prediction_made || prediction.created_at
      };
      
      // Simple bounds checking for horizontal top stack
      const actualTweetWidth = baseCardWidth * scaleFactor * screenshotBoost;
      const chartXRange = xScale.range();
      const availableChartWidth = Math.max(...chartXRange) - Math.min(...chartXRange);
      const finalTweetX = Math.max(leftStartMargin, Math.min(tweetX, availableChartWidth - actualTweetWidth - 20));
      
      console.log('Tweet positioning:', {
        x: finalTweetX,
        y: tweetY,
        scaleFactor,
        expert: tweetData.expert_name
      });

      // 🎯 ADAPTIVE RENDERING: Only show tweets based on zoom level
      let actualCardHeight = 80; // Default height for minimal modes
      
      // Simplified adaptive check: Use time span to determine detail level
      const xDomain = xScale.domain();
      const timeSpan = new Date(xDomain[1]).getTime() - new Date(xDomain[0]).getTime();
      const daySpanForAdaptive = timeSpan / (1000 * 60 * 60 * 24);
      
      // SIMPLIFIED: Always show tweets and labels - no complex adaptive logic
      const currentAdaptiveConfig = {
        level: 'DETAILED',
        showLabels: true,
        showTweets: true,  // Always show tweets!
        showDescriptions: true,
        iconSize: 12,
        fontSize: '12px',
        spacing: 40
      };
      
      if (currentAdaptiveConfig.showTweets) {
        // Create standard tweet visualization
        const { cardGroup: tweetViz, cardHeight: calculatedHeight } = createTweetVisualization(
          tweetGroup,
          tweetData,
          finalTweetX,
          tweetY,
          theme,
          baseCardWidth,
          undefined,
          currentAdaptiveConfig.level === 'DETAILED' ? scaleFactor : Math.min(scaleFactor, 0.8)
        );
        
        actualCardHeight = calculatedHeight;
        
        // ========== DRAGGABLE CARDS WITH RESPONSIVE LINES ==========
        // Store card state for drag operations
        let cardOffsetX = 0;
        let cardOffsetY = 0;
        let connectionLine: d3.Selection<SVGPathElement, unknown, null, undefined> | null = null;
        
        // Function to calculate connection line path
        const calculateConnectionPath = (cardX: number, cardY: number, cardW: number, cardH: number) => {
          const cornerRadius = 8; // Must match TweetVisualization BASE.cornerRadius
          const cardLeft = cardX;
          const cardRight = cardX + cardW;
          const cardTop = cardY;
          const cardBottom = cardY + cardH;
          const chartDotX = xPos;
          const chartDotY = yPos;
          
          let startX: number, startY: number;
          
          // Find closest edge point to the prediction dot
          if (chartDotX < cardLeft) {
            startX = cardLeft;
            startY = Math.max(cardTop + cornerRadius, Math.min(cardBottom - cornerRadius, chartDotY));
          } else if (chartDotX > cardRight) {
            startX = cardRight;
            startY = Math.max(cardTop + cornerRadius, Math.min(cardBottom - cornerRadius, chartDotY));
          } else if (chartDotY < cardTop) {
            startX = Math.max(cardLeft + cornerRadius, Math.min(cardRight - cornerRadius, chartDotX));
            startY = cardTop;
          } else if (chartDotY > cardBottom) {
            startX = Math.max(cardLeft + cornerRadius, Math.min(cardRight - cornerRadius, chartDotX));
            startY = cardBottom;
          } else {
            startX = cardRight;
            startY = cardTop + cardH / 2;
          }
          
          // Handle corner cases - if we're in a corner region, adjust to the corner arc
          const isNearLeftEdge = chartDotX <= cardLeft + cornerRadius;
          const isNearRightEdge = chartDotX >= cardRight - cornerRadius;
          const isNearTopEdge = chartDotY <= cardTop + cornerRadius;
          const isNearBottomEdge = chartDotY >= cardBottom - cornerRadius;
          
          // Top-left corner
          if (chartDotX < cardLeft && chartDotY < cardTop + cornerRadius) {
            const centerX = cardLeft + cornerRadius;
            const centerY = cardTop + cornerRadius;
            const angle = Math.atan2(chartDotY - centerY, chartDotX - centerX);
            startX = centerX + cornerRadius * Math.cos(angle);
            startY = centerY + cornerRadius * Math.sin(angle);
          }
          // Top-right corner
          else if (chartDotX > cardRight && chartDotY < cardTop + cornerRadius) {
            const centerX = cardRight - cornerRadius;
            const centerY = cardTop + cornerRadius;
            const angle = Math.atan2(chartDotY - centerY, chartDotX - centerX);
            startX = centerX + cornerRadius * Math.cos(angle);
            startY = centerY + cornerRadius * Math.sin(angle);
          }
          // Bottom-left corner
          else if (chartDotX < cardLeft && chartDotY > cardBottom - cornerRadius) {
            const centerX = cardLeft + cornerRadius;
            const centerY = cardBottom - cornerRadius;
            const angle = Math.atan2(chartDotY - centerY, chartDotX - centerX);
            startX = centerX + cornerRadius * Math.cos(angle);
            startY = centerY + cornerRadius * Math.sin(angle);
          }
          // Bottom-right corner
          else if (chartDotX > cardRight && chartDotY > cardBottom - cornerRadius) {
            const centerX = cardRight - cornerRadius;
            const centerY = cardBottom - cornerRadius;
            const angle = Math.atan2(chartDotY - centerY, chartDotX - centerX);
            startX = centerX + cornerRadius * Math.cos(angle);
            startY = centerY + cornerRadius * Math.sin(angle);
          }
          
          const dist = Math.sqrt(Math.pow(chartDotX - startX, 2) + Math.pow(chartDotY - startY, 2));
          
          if (dist > 50) {
            const midX = (startX + chartDotX) / 2;
            const midY = (startY + chartDotY) / 2;
            const curveX = chartDotX > startX ? 20 : -20;
            return `M ${startX} ${startY} Q ${midX + curveX} ${midY} ${chartDotX} ${chartDotY}`;
          }
          return `M ${startX} ${startY} L ${chartDotX} ${chartDotY}`;
        };
        
        // Add drag behavior
        const dragBehavior = d3.drag<SVGGElement, unknown>()
          .filter(function(event) {
            // Don't start drag if clicking on close button or other interactive elements
            const target = event.target as Element;
            if (target.closest('.close-button') || target.closest('.source-button') || target.closest('.share-button')) {
              return false;
            }
            return true;
          })
          .on('start', function(event) {
            // Store starting offset
            const transform = d3.select(this).attr('transform');
            if (transform) {
              const match = transform.match(/translate\(([^,]+),\s*([^)]+)\)/);
              if (match) {
                cardOffsetX = parseFloat(match[1]);
                cardOffsetY = parseFloat(match[2]);
              }
            }
            // Bring card to front
            if (this.parentNode) {
              d3.select(this.parentNode as Element).raise();
            }
            // Visual feedback
            d3.select(this).select('.tweet-card-bg')
              .attr('stroke', '#F7931A')
              .attr('stroke-width', 2);
          })
          .on('drag', function(event) {
            // Update position
            cardOffsetX += event.dx;
            cardOffsetY += event.dy;
            d3.select(this).attr('transform', `translate(${cardOffsetX}, ${cardOffsetY})`);
            
            // Update connection line in real-time
            if (connectionLine) {
              const newPath = calculateConnectionPath(
                finalTweetX + cardOffsetX,
                tweetY + cardOffsetY,
                actualTweetWidth,
                actualCardHeight
              );
              connectionLine.attr('d', newPath);
            }
          })
          .on('end', function() {
            // Reset visual feedback
            d3.select(this).select('.tweet-card-bg')
              .attr('stroke', theme.tweet.borderColor)
              .attr('stroke-width', 1);
            
            // Store position in localStorage for persistence (optional)
            const posKey = `tweetPos_${prediction.id}`;
            localStorage.setItem(posKey, JSON.stringify({ x: cardOffsetX, y: cardOffsetY }));
          });
        
        // Apply drag behavior to the card
        tweetViz.call(dragBehavior as any);
        tweetViz.style('cursor', 'grab');
        
        // Load saved position if exists - but constrain to visible area
        const savedPos = localStorage.getItem(`tweetPos_${prediction.id}`);
        if (savedPos) {
          try {
            const pos = JSON.parse(savedPos);
            // Get chart bounds
            const chartYRange = yScale.range();
            const maxY = Math.max(...chartYRange) - actualCardHeight - 20;
            const maxX = availableChartWidth - actualTweetWidth - 20;
            
            // Constrain saved position to visible area
            cardOffsetX = Math.max(-finalTweetX + 20, Math.min(pos.x || 0, maxX - finalTweetX));
            cardOffsetY = Math.max(-tweetY + 20, Math.min(pos.y || 0, maxY - tweetY));
            
            tweetViz.attr('transform', `translate(${cardOffsetX}, ${cardOffsetY})`);
          } catch (e) { /* ignore parse errors */ }
        }
        
        // Create connection line (store reference for drag updates)
        const lineWidth = currentAdaptiveConfig.level === 'DETAILED' ? 2 : 1;
        connectionLine = tweetGroup.append('path')
          .attr('d', calculateConnectionPath(
            finalTweetX + cardOffsetX,
            tweetY + cardOffsetY,
            actualTweetWidth,
            actualCardHeight
          ))
          .attr('stroke', 'rgba(247, 147, 26, 0.6)')
          .attr('stroke-width', lineWidth)
          .attr('fill', 'none')
          .attr('stroke-dasharray', '4,3')
          .attr('class', 'tweet-connection')
          .style('pointer-events', 'none');
          
      } else {
        // MINIMAL/COMPACT modes: Just show a small sentiment indicator
        const sentimentIndicator = tweetGroup.append('g')
          .attr('class', 'sentiment-indicator');
        
        // Small colored circle
        sentimentIndicator.append('circle')
          .attr('cx', finalTweetX + 15)
          .attr('cy', tweetY + 15)
          .attr('r', currentAdaptiveConfig.iconSize)
          .attr('fill', '#F7931A')
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 2)
          .attr('opacity', 0.8);
        
        // Tweet emoji for compact mode
        if (currentAdaptiveConfig.level === 'COMPACT') {
          sentimentIndicator.append('text')
            .attr('x', finalTweetX + 15)
            .attr('y', tweetY + 18)
            .attr('text-anchor', 'middle')
            .attr('font-size', '8px')
            .text('💬');
          
          // Abbreviated name for high priority tweets
          const priority = getPredictionPriority(prediction);
          if (priority >= 2) {
            const name = prediction.influencer?.name || prediction.expert_name || '';
            const abbrev = name.split(' ').map(word => word[0]).join('').substring(0, 2);
            
            sentimentIndicator.append('text')
              .attr('x', finalTweetX + 15)
              .attr('y', tweetY + 35)
              .attr('text-anchor', 'middle')
              .attr('font-size', currentAdaptiveConfig.fontSize)
              .attr('fill', theme.text)
              .text(abbrev);
          }
        }
        
        actualCardHeight = 30; // Small height for minimal modes
      }

      // Store position for stacking system
      (window.tweetPositions as any[]).push({
        x: finalTweetX,
        y: tweetY,
        width: actualTweetWidth,
        height: actualCardHeight,
        index: index,
        isBet: false // All tweets here are non-bet
      });

      // Connection line is now created inside the draggable card section above
        
      // Add price point marker (adaptive size)
      tweetGroup.append('circle')
        .attr('cx', xPos)
        .attr('cy', yPos)
        .attr('r', currentAdaptiveConfig.iconSize / 2)
        .attr('fill', '#F7931A') // Bitcoin orange
        .attr('stroke', '#ffffff')
        .attr('stroke-width', 1)
        .attr('class', 'price-marker')
        .style('pointer-events', 'none');

      
    } catch (error) {
      console.error(`Error rendering tweet visualization for prediction ${prediction.id}:`, error);
    }
  });

  // Render range predictions using dedicated renderer
  renderRangePredictions({
    chartArea,
    predictions,
    activePredictions,
    xScale,
    yScale,
    theme
  });

  // Render price-time range predictions
  renderPriceTimeRangePredictions({
    chartArea,
    predictions,
    activePredictions,
    xScale,
    yScale,
    theme
  });

  // Render successful predictions highlighting
  renderSuccessfulPredictions({
    chartArea,
    predictions,
    activePredictions,
    xScale,
    yScale,
    theme
  });
};

// This function is not currently used, but kept for reference
const renderPrediction = (prediction: Prediction, options: RenderOptions) => {
  // ...existing code...
};